/**
 * API Client — connects frontend to Lambda backend
 *
 * Features:
 * - Automatic X-Entity-Id header injection from active entity
 * - JWT token injection from Cognito auth
 * - Centralized error handling
 * - Offline detection + queue notification
 * - Falls back to in-process agents when no backend is configured
 */

import { useAppStore } from '@/stores/useAppStore';
import { getAccessToken, signOut } from '@/lib/auth';
import { httpToAppError, NetworkError } from '@/lib/errors';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const API_TIMEOUT_MS = 30000; // 30s timeout for API calls

// If no API base URL is configured, we're in local/demo mode
const isLocalMode = !API_BASE;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown>;
  entityId?: string; // Override active entity
  skipAuth?: boolean;
  skipEntityHeader?: boolean; // For routes that don't need X-Entity-Id
  timeoutMs?: number; // Override default timeout
}

// --- HR (Phase 2a) ---
export type LeaveType = 'pto' | 'vacation' | 'sick' | 'personal' | 'bereavement'
  | 'jury_duty' | 'fmla' | 'maternity' | 'paternity' | 'military' | 'holiday' | 'other';

export type PTORequestStatus = 'draft' | 'submitted' | 'approved' | 'denied' | 'taken' | 'canceled';

export type EmploymentType = 'w2_full_time' | 'w2_part_time' | 'statutory' | 'intern' | 'seasonal';
export type EmploymentStatus = 'active' | 'on_leave' | 'terminated' | 'retired';

export interface EmployeeRow {
  id: string;
  entity_id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  hire_date: string | null;
  termination_date: string | null;
  employment_type: EmploymentType;
  status: EmploymentStatus;
  title: string | null;
  compensation_amount: string | null;
  compensation_period: string | null;
  exempt: boolean;
  i9_verified: boolean;
  i9_reverification_due: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmployeeFull extends EmployeeRow {
  // Sensitive PII. The server NEVER returns the full SSN (go-live security
  // audit 2026-06-28, P1-2) — only the last 4 digits, for display. The full
  // SSN flows to the payroll provider via the export path, not the browser.
  ssnLast4?: string | null;
  /** @deprecated server no longer returns the full SSN; use ssnLast4 */
  ssn?: string | null;
  dob?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  zip?: string | null;
  country?: string;
  pay_schedule?: string | null;
  w4_data?: Record<string, unknown>;
  state_w4_data?: Record<string, unknown>;
  i9_verified_date?: string | null;
  i9_document_type?: string | null;
  i9_document_details?: Record<string, unknown>;
  direct_deposit_setup?: boolean;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relationship?: string | null;
  notes?: string | null;
}

// NS4 Week 3 — Payroll state allocation (migration 070).
// Per-employee per-pay-period split of gross wages across states.
export interface PayrollStateAllocation {
  id: string;
  employee_id: string;
  pay_period_id: string;
  state: string;
  days_worked: string | null;
  hours_worked: string | null;
  gross_wages: string;
  state_withholding: string | null;
  source: 'manual' | 'time_entries_auto' | 'imported';
  notes: string | null;
  // joined from employees on list
  first_name?: string;
  last_name?: string;
  email?: string | null;
}

// NS4 followup R6 — Employee onboarding checklist (migration 072).
export interface EmployeeOnboardingItem {
  id: string;
  entity_id: string;
  employee_id: string;
  category: string;
  item_key: string;
  title: string;
  description: string | null;
  external_link: string | null;
  status: 'pending' | 'in_progress' | 'complete' | 'blocked' | 'not_applicable';
  completed_at: string | null;
  completed_by_user_id: string | null;
  notes: string | null;
  blocked_reason: string | null;
  is_custom: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// NS4 Week 3 — Retention attestation (migration 069). Operator's
// annual record that they uphold the IRS 7-year retention obligation.
export interface RetentionAttestation {
  id: string;
  tax_year: number;
  attester_user_id: string;
  attester_role: string;
  attester_name: string | null;
  notes: string | null;
  scope: Record<string, unknown>;
  status: 'current' | 'superseded' | 'revoked';
  created_at: string;
  updated_at: string;
}

// Employee Portal link state (S25 Phase 1, S26 Phase 2).
// Returned alongside the employee record from GET /employees/:id so the
// HR detail page can render Invite / Resend / Revoke / Terminate buttons
// without an extra round trip.
export interface EmployeePortalLink {
  id: string;
  status: 'invited' | 'active' | 'disabled' | 'terminated';
  email: string;
  expiresAt: string | null;
  invitedAt: string | null;
  activatedAt: string | null;
  terminatedAt: string | null;
  terminatedReason: string | null;
}

export interface PTOBalance {
  leave_type: LeaveType;
  balance_hours: string;
  accrued_to_date: string;
  used_to_date: string;
  last_event_date: string | null;
}

export interface PTOAccrualEntry {
  id: string;
  leave_type: LeaveType;
  hours: string;
  effective_date: string;
  source: string;
  request_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface PTORequest {
  id: string;
  entity_id: string;
  employee_id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  hours_requested: string;
  reason: string | null;
  status: PTORequestStatus;
  submitted_at: string;
  decided_at: string | null;
  decision_notes: string | null;
}

// Scheduling Phase B-E (2026-07-14).
export interface ScheduleConflict {
  id: string;
  work_date: string;
  work_type: string;
  facility_name: string | null;
  location: string | null;
  // Round-3 audit fix: detectScheduleConflicts now also merges in the
  // employee's connected personal calendar (Calendar Sync's whole point —
  // "spot conflicts before you block time off"), tagged so a caller can tell
  // a Nexus shift apart from a personal event if it wants to. Optional so
  // any existing consumer that renders work_type/facility_name generically
  // (both fields stay populated for both kinds) needs no changes.
  source?: 'shift' | 'personal_calendar';
}

export interface EmployeePTORequest {
  id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  hours_requested: string;
  reason: string | null;
  status: PTORequestStatus;
  submitted_at: string;
  decided_at: string | null;
  decision_notes: string | null;
}

export interface ManagerPTORequest extends EmployeePTORequest {
  employee_id: string;
  first_name: string | null;
  last_name: string | null;
  conflicts: ScheduleConflict[];
}

export interface CapacityDay { date: string; open: boolean }
export interface CapacityEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  openDayCount: number;
  days: CapacityDay[];
}

export interface CombinedScheduleEntity {
  entityId: string;
  entityName: string | null;
  shifts: Array<{
    id: string;
    work_date: string;
    work_type: string;
    facility_name: string | null;
    employee_id: string;
    status: string;
  }>;
}

export type CalendarConnectionProvider = 'google' | 'apple';
export type CalendarConnectionStatus = 'connected' | 'needs_reauth' | 'disconnected';
export interface CalendarConnectionCalendar { id: string; displayName: string; color: string }
export interface CalendarConnection {
  id: string;
  provider: CalendarConnectionProvider;
  status: CalendarConnectionStatus;
  apple_account_email: string | null;
  selected_calendars: CalendarConnectionCalendar[];
  has_synced_before: boolean;
  last_synced_at: string | null;
  last_sync_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface PTOStateRule {
  state: string;
  name: string;
  mandatorySickHours: number;
  accrualMethod: 'annual_grant' | 'accrual' | 'discretionary';
  accrualHoursPerYear: number;
  carryoverCapHours: number;
  rationale: string;
}

export interface HolidayEntry {
  id: string;
  entity_id: string;
  observed_date: string;
  name: string;
  jurisdiction: string;
  is_paid: boolean;
  is_floating: boolean;
  notes: string | null;
  created_at: string;
}

// --- Projects (Phase 2c) ---
export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'canceled';
export type BillingType = 'fixed_fee' | 'time_and_materials' | 'retainer' | 'milestone' | 'internal';

export interface Project {
  id: string;
  entity_id: string;
  client_id: string | null;
  name: string;
  code: string | null;
  description: string | null;
  status: ProjectStatus;
  billing_type: BillingType;
  start_date: string | null;
  end_date: string | null;
  expected_end_date: string | null;
  budget_amount: string | null;
  budget_hours: string | null;
  default_billing_rate: string | null;
  lead_user_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectProfitability {
  project_id: string;
  entity_id: string;
  name: string;
  code: string | null;
  status: ProjectStatus;
  billing_type: BillingType;
  budget_amount: string | null;
  budget_hours: string | null;
  revenue_collected: string | number;
  revenue_pipeline: string | number;
  expenses_total: string | number;
  bills_paid: string | number;
  bills_outstanding: string | number;
  contractor_cost: string | number;
  time_cost: string | number;
  hours_logged: string | number;
  total_cost: number;
  margin: number;
  margin_pct: number | null;
}

// --- Owner Continuity (Phase 2d) ---
// Backend rows are snake_case; api layer types mirror that so page
// code doesn't need to guess camelCase vs snake_case on responses.
// WRITE payloads (createXxx / upsertXxx) take camelCase and the
// handler maps it server-side.
export type ContinuityPlanStatus = 'draft' | 'in_progress' | 'current' | 'stale' | 'archived';
export type ContinuityDocType =
  | 'operating_agreement' | 'will' | 'living_will' | 'poa_financial' | 'poa_medical'
  | 'revocable_trust' | 'irrevocable_trust' | 'buy_sell_agreement' | 'ownership_cert'
  | 'insurance_policy' | 'bank_access' | 'brokerage_access' | 'safe_deposit'
  | 'password_manager' | 'digital_asset_inventory' | 'ein_letter' | 'state_registration'
  | 'beneficiary_designation' | 'deed_or_title' | 'mortgage' | 'lease' | 'other';
export type BeneficiaryAssetType =
  | 'llc_interest' | 'insurance_life' | 'insurance_disability'
  | 'retirement_401k' | 'retirement_ira' | 'retirement_roth'
  | 'brokerage_account' | 'bank_account' | 'real_estate' | 'business_interest' | 'other';
export type BeneficiaryType = 'primary' | 'contingent' | 'tertiary';
export type EmergencyRole =
  | 'spouse' | 'executor' | 'attorney' | 'cpa' | 'financial_advisor' | 'insurance_agent'
  | 'banker' | 'bookkeeper' | 'board_member' | 'business_partner' | 'physician'
  | 'clergy' | 'family' | 'key_employee' | 'successor_owner' | 'power_of_attorney' | 'other';

export interface ContinuityPlan {
  id: string;
  entity_id: string;
  status: ContinuityPlanStatus;
  owner_instructions: string | null;
  review_interval_months: number;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  // Camel-case variants exposed on write path only
  ownerInstructions?: string | null;
  reviewIntervalMonths?: number;
}

export interface ContinuityDocument {
  id: string;
  entity_id: string;
  plan_id: string | null;
  doc_type: ContinuityDocType;
  title: string;
  location: string | null;
  custodian_name: string | null;
  custodian_contact: string | null;
  document_date: string | null;
  expiration_date: string | null;
  reference_number: string | null;
  notes: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  // camelCase for create/update payloads
  docType?: ContinuityDocType;
  custodianName?: string | null;
  custodianContact?: string | null;
  documentDate?: string | null;
  expirationDate?: string | null;
  referenceNumber?: string | null;
}

export interface BeneficiaryDesignation {
  id: string;
  entity_id: string;
  plan_id: string | null;
  asset_type: BeneficiaryAssetType;
  asset_description: string;
  asset_reference: string | null;
  beneficiary_type: BeneficiaryType;
  beneficiary_name: string;
  beneficiary_relationship: string | null;
  beneficiary_contact: string | null;
  share_percent: string | null;
  notes: string | null;
  designated_date: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  // camelCase write path
  assetType?: BeneficiaryAssetType;
  assetDescription?: string;
  assetReference?: string | null;
  beneficiaryType?: BeneficiaryType;
  beneficiaryName?: string;
  beneficiaryRelationship?: string | null;
  beneficiaryContact?: string | null;
  sharePercent?: number | null;
  designatedDate?: string | null;
}

export interface EmergencyContact {
  id: string;
  entity_id: string;
  plan_id: string | null;
  role: EmergencyRole;
  name: string;
  firm: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  relationship_note: string | null;
  call_order: number | null;
  authority_notes: string | null;
  notes: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  relationshipNote?: string | null;
  callOrder?: number | null;
  authorityNotes?: string | null;
}

export interface BuySellTerms {
  id: string;
  entity_id: string;
  plan_id: string | null;
  has_agreement: boolean;
  agreement_date: string | null;
  agreement_location: string | null;
  valuation_method: string | null;
  valuation_formula: string | null;
  last_valuation_amount: string | null;
  last_valuation_date: string | null;
  valuation_review_interval_months: number | null;
  trigger_death: boolean;
  trigger_disability: boolean;
  trigger_retirement: boolean;
  trigger_divorce: boolean;
  trigger_voluntary: boolean;
  trigger_bankruptcy: boolean;
  trigger_notes: string | null;
  funding_method: string | null;
  funding_details: string | null;
  key_person_insurance_in_force: boolean;
  key_person_policy_reference: string | null;
  key_person_coverage_amount: string | null;
  key_person_carrier: string | null;
  notes: string | null;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  created_at: string;
  updated_at: string;
  // camelCase write fields
  hasAgreement?: boolean;
  agreementDate?: string | null;
  agreementLocation?: string | null;
  valuationMethod?: string | null;
  valuationFormula?: string | null;
  lastValuationAmount?: number | null;
  lastValuationDate?: string | null;
  valuationReviewIntervalMonths?: number | null;
  triggerDeath?: boolean;
  triggerDisability?: boolean;
  triggerRetirement?: boolean;
  triggerDivorce?: boolean;
  triggerVoluntary?: boolean;
  triggerBankruptcy?: boolean;
  triggerNotes?: string | null;
  fundingMethod?: string | null;
  fundingDetails?: string | null;
  keyPersonInsuranceInForce?: boolean;
  keyPersonPolicyReference?: string | null;
  keyPersonCoverageAmount?: number | null;
  keyPersonCarrier?: string | null;
}

export interface ContinuityStatusRow {
  plan_id: string;
  entity_id: string;
  status: ContinuityPlanStatus;
  review_interval_months: number;
  last_reviewed_at: string | null;
  plan_review_overdue: boolean;
  documents_count: number;
  documents_stale_count: number;
  beneficiaries_count: number;
  contacts_count: number;
  has_buy_sell: boolean | null;
}

export interface ContinuityBundle {
  plan: ContinuityPlan;
  documents: ContinuityDocument[];
  beneficiaries: BeneficiaryDesignation[];
  contacts: EmergencyContact[];
  buySell: BuySellTerms | null;
  status: ContinuityStatusRow;
}

// --- Tax Optimizer (S30B + S31) ---
//
// Citation registry: every constant + knob carries IRC / IRS Pub references.
// `key` is the registry identifier (e.g. 'IRC_199A'); `code` is the human
// label (e.g. 'IRC §199A'). The registry is loaded once via getTaxCitations()
// and cached by the UI; per-scenario citations come back inline on optimize()
// and are persisted in tax_scenarios.snapshot_citations.

export interface TaxCitationRecord {
  key: string;
  code: string;
  title: string;
  summary: string;
  sourceUrl: string;
  irsPubs: string[];
  lastVerified: string;
}

export interface KnobCitation {
  primary: string;
  supporting: string[];
  rationale: string;
  riskLevel: 'low' | 'medium' | 'high';
  requiresDocumentation: string[];
  citations: TaxCitationRecord[];
}

export interface TaxRecommendation {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string;
  cta: string;
  citations: TaxCitationRecord[];
}

export interface StateRuleApplied {
  stateCode: string;
  filerType: 'default' | 'corp' | 'pit' | 'pass_through';
  bonus_position: string;
  section_179_cap_cents: number | null;
  citation: string;
  primary_source_url: string;
  state_form_name: string | null;
  filer_type_fallback: boolean;
  is_fallback: boolean;
  registryVersion: string;
}

// PP2 — S-corp break-even comparison. Mirrors optimizer.compareElections +
// the endpoint's suggested-defaults envelope.
export interface BreakEvenResponse {
  result: {
    inputs: {
      businessNetIncome: number;
      otherTaxableIncome: number;
      filingStatus: string;
      state: string;
      stateRatePct: number;
      reasonableCompWage: number;
      isSstb: boolean | null;
    };
    soleProp: {
      seTax: number; halfSEDeduction: number; qbiDeduction: number;
      federalTax: number; stateTax: number; totalTax: number;
    };
    sCorp: {
      wage: number; distribution: number;
      employeeAndEmployerFica: number; employerFicaDeducted: number;
      qbiDeduction: number; federalTax: number; stateTax: number;
      taxTotal: number; adminCosts: number; allInCost: number;
    };
    cCorp: {
      wage: number; corporateTaxableIncome: number; corporateTax: number;
      dividendDistributed: number; employeeAndEmployerFica: number;
      ownerOrdinaryFederalTax: number; dividendTax: number; stateTax: number;
      taxTotal: number;
    };
    lowestTaxElection: 'soleProp' | 'sCorp' | 'cCorp';
    taxSavings: number;
    adminCosts: number;
    netSavings: number;
    verdict: 'need_wage' | 'worth_it' | 'marginal' | 'not_worth_it';
    caveats: string[];
    disclaimer: string;
  };
  suggested: {
    netIncome: number;
    wage: number | null;
    filingStatus: string;
    otherIncome: number;
    isSstb: boolean | null;
    stateRatePct: number | null;
    currentElection: string | null;
    fmvSpecialty: string | null;
  };
}

export interface TaxOptimizerResponse {
  base: Record<string, unknown>;
  scenario: Record<string, unknown>;
  delta: {
    dFederal: number; dAMT: number; dState: number; dSE: number;
    dTotalTax: number; dQBI: number; dTaxableIncome: number;
    dGrossIncome: number; dBalanceDue: number; dSolo401kHeadroom: number;
    netCashImpact: number;
    // Bundle 5.1b-b Phase 2:
    dStateDepreciation: number;
    dStateAddBack: number;
  };
  knobs: Record<string, unknown>;
  scenarioInputs: Record<string, unknown>;
  citations: Record<string, KnobCitation>;
  recommendations: TaxRecommendation[];
  citationVersion: string;
  disclaimer: string;
}

// Bundle 5.1b-b Phase 3: entities.filer_type override response shape.
export interface EntityFilerTypeResponse {
  entityId: string;
  taxElection: string | null;
  stateOfFormation: string | null;
  homeState: string | null;
  filerType: 'default' | 'corp' | 'pit' | 'pass_through' | null;
  filerTypeIsOverride: boolean;
  validValues: Array<'default' | 'corp' | 'pit' | 'pass_through'>;
}

export type TaxScenarioStatus = 'draft' | 'saved' | 'applied' | 'archived';

export interface TaxScenario {
  id: string;
  entityId: string;
  createdByUserId: string;
  taxYear: number;
  name: string;
  description: string | null;
  knobs: Record<string, number | string>;
  // Snapshots are present on getTaxScenario but trimmed off list responses
  // (the list endpoint does NOT select the snapshot columns to keep payloads
  // small — the UI fetches the full row only when a scenario is opened).
  snapshotBase?: Record<string, unknown> | null;
  snapshotScenario?: Record<string, unknown> | null;
  snapshotDelta?: TaxOptimizerResponse['delta'] | null;
  snapshotCitations?: Record<string, KnobCitation> | null;
  snapshotRecs?: TaxRecommendation[] | null;
  snapshotInputs?: Record<string, unknown> | null;
  citationVersion: string | null;
  status: TaxScenarioStatus;
  appliedAt: string | null;
  appliedByUserId: string | null;
  appliedNote: string | null;
  isStarred: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaxCitationRegistry {
  citationVersion: string;
  citations: Record<string, TaxCitationRecord>;
  constants: Record<string, KnobCitation>;
  knobs: Record<string, Omit<KnobCitation, 'citations'>>;
  recommendations: Array<{
    id: string;
    severity: 'info' | 'warning' | 'critical';
    title: string;
    body: string;
    cta: string;
    citationKeys: string[];
  }>;
}

// S32 C2: white-label advisor console types
export interface AdvisorClient {
  orgId: string;
  orgName: string;
  subscriptionTier: string | null;
  orgCreatedAt: string;
  linkRole: 'advisor' | 'advisor_admin';
  grantedAt: string;
  entityCount: number;
  entityNames: string[];
}

export interface AdvisorClientDetail {
  org: {
    id: string;
    name: string;
    subscriptionTier: string | null;
    createdAt: string;
  };
  entities: Array<{
    id: string;
    legalName: string;
    taxElection: string | null;
    specialty: string | null;
    compensationModel: string | null;
    createdAt: string;
  }>;
}

export interface AdvisorPortfolio {
  summary: {
    clientOrgCount: number;
    entityCount: number;
    activeSubscriptions: number;
  };
  clients: Array<{
    orgId: string;
    orgName: string;
    entityCount: number;
    subscriptionTier: string | null;
  }>;
  generatedAt: string;
}

export interface AdvisorBrandSettings {
  firmName: string | null;
  logoS3Key: string | null;
  brandColorHex: string | null;
  customSubdomain: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  updatedAt?: string;
}

// S33 P0 C2 Phase 2: per-entity drill-in shapes
export interface AdvisorClientEntities {
  org: { id: string; name: string; subscriptionTier: string | null };
  year: number;
  entities: Array<{
    id: string;
    legalName: string;
    taxElection: string | null;
    specialty: string | null;
    compensationModel: string | null;
    createdAt: string;
    summary: { revenue: number; expenses: number; netIncome: number; error?: string };
  }>;
}

export interface AdvisorEntityDrillIn {
  entity: {
    id: string;
    legalName: string;
    taxElection: string | null;
    specialty: string | null;
    compensationModel: string | null;
    orgId: string;
    createdAt: string;
  };
  year: number;
  pl: { revenue?: number; expenses?: number; deductibleExpenses?: number; wages?: number; employerTaxes?: number; grossProfit?: number; netIncome?: number; error?: string };
  cashflow: { startingBalance?: number; expectedInflow?: number; expectedOutflow?: number; expectedNet?: number; endingBalance?: number; openInvoiceCount?: number; openBillCount?: number; anchorDate?: string | null; anchorAccount?: string | null; error?: string };
  arap: { arCurrent?: number; ar30?: number; ar60?: number; ar90?: number; arOver90?: number; arTotal?: number; arOpenCount?: number; apCurrent?: number; ap30?: number; ap60?: number; ap90?: number; apOver90?: number; apTotal?: number; apOpenCount?: number; netWorkingCapital?: number; error?: string };
  // S33 Session C: linkRole surfaces so the frontend can gate write affordances
  linkRole?: 'advisor' | 'advisor_admin';
  generatedAt: string;
}

// --- Finance: Bills (Phase 2b) ---
export type BillStatus = 'draft' | 'received' | 'scheduled' | 'partial' | 'paid' | 'void' | 'disputed' | 'overdue';
export type BillPaymentMethod = 'ach' | 'check' | 'wire' | 'card' | 'cash' | 'other';

export interface Bill {
  id: string;
  entity_id: string;
  vendor_name: string;
  vendor_id: string | null;
  vendor_email: string | null;
  bill_number: string | null;
  bill_date: string;
  due_date: string;
  received_date: string | null;
  subtotal: string;
  tax_rate: string;
  tax_amount: string;
  total: string;
  amount_paid: string;
  currency: string;
  category: string | null;
  irc_code: string | null;
  irc_description: string | null;
  deductibility_pct: number;
  status: BillStatus;
  scheduled_payment_date: string | null;
  source_doc_s3_path: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Migration 033 — 1099-MISC rent support
  is_rent?: boolean;
  payee_contractor_id?: string | null;
  // Migration 055 — intercompany flag (Phase Three #1)
  intercompany_target_entity_id?: string | null;
  // Migration 077 — IRC §3406 backup withholding (P1.4 audit Finding A-11).
  backup_withholding_cents?: number;
  backup_withholding_decision?: 'applied' | 'declined' | 'not_applicable' | null;
  backup_withholding_recorded_at?: string | null;
  backup_withholding_recorded_by?: string | null;
}

// Migration 033 + S30 (migration 052): classification disambiguates which
// 1099 form type a payee is on. service/landlord/royalty/prize/other landed
// in S16; interest_recipient/shareholder/distributee landed in S30 to drive
// 1099-INT/DIV/R recipient pickers respectively.
export type ContractorClassification =
  | 'service'             // 1099-NEC default
  | 'landlord'            // 1099-MISC Box 1 rents
  | 'royalty'             // 1099-MISC Box 2
  | 'prize'               // 1099-MISC Box 3
  | 'other'               // catch-all
  | 'interest_recipient'  // 1099-INT
  | 'shareholder'         // 1099-DIV
  | 'distributee';        // 1099-R

export interface BillLineItem {
  id: string;
  entity_id: string;
  bill_id: string;
  description: string;
  quantity: string;
  unit_price: string;
  amount: string;
  tax_rate: string | null;
  category: string | null;
  irc_code: string | null;
  created_at: string;
}

export interface BillPayment {
  id: string;
  entity_id: string;
  bill_id: string;
  amount: string;
  payment_date: string;
  payment_method: BillPaymentMethod;
  reference_number: string | null;
  notes: string | null;
  created_at: string;
}

// Owner-draw ledger (A2 — Home Redesign 2026-06-02). amount_cents is a pg BIGINT
// (serializes as a string over JSON).
export interface OwnerDistribution {
  id: string;
  entity_id: string;
  user_id: string;
  distribution_date: string;
  amount_cents: string | number;
  method: 'transfer' | 'check' | 'ach' | 'wire' | 'cash' | 'other';
  note: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// Expected-revenue-from-schedule summary (Home Redesign). All *_cents are integer cents.
export interface ExpectedRevenueSummary {
  today: string;
  includedCount: number;
  thisWeekCents: number;
  thisMonthCents: number;
  thisQuarterCents: number;
  totalUpcomingCents: number;
  byConfidence: { confirmedCents: number; scheduledCents: number };
  byFacility: Array<{ facility: string; cents: number; days: number }>;
}

// Personal "You" lens cross-entity rollup (Home Redesign). All *Cents are integer cents.
// `tax` / `estimates` / `retirement` are the raw tax-planning-agent objects (v1, primary entity).
export interface PersonalOverview {
  year: number;
  household: boolean;
  income: {
    llcWagesCents: number;
    profitShareCents: number;
    distributionsCashCents: number;
    outsideW2Cents: number;
    investmentCents: number;
    totalIncomeCents: number;
  };
  byEntity: Array<{
    entityId: string;
    name: string;
    taxElection: string | null;
    wagesCents: number;
    profitShareCents: number;
    drawsTakenCents: number;
    leftInBusinessCents: number;
  }>;
  draws: { totalCents: number };
  tax: unknown | null;
  estimates: unknown | null;
  retirement: unknown | null;
  firewall: string;
  taxScope: string;
}

export interface APAgingBuckets {
  entity_id: string;
  current_count: number;
  current_amount: string | number;
  bucket_1_30_count: number;
  bucket_1_30_amount: string | number;
  bucket_31_60_count: number;
  bucket_31_60_amount: string | number;
  bucket_61_90_count: number;
  bucket_61_90_amount: string | number;
  bucket_90plus_count: number;
  bucket_90plus_amount: string | number;
  total_outstanding: string | number;
}

export interface CashFlowWeek {
  weekStart: string;
  weekEnd: string;
  expectedInflow: number;
  expectedOutflow: number;
  net: number;
  runningBalance: number;
}

// --- Contracts + unified documents (Phase 2e, migration 030) ---
export type ContractType =
  | 'lease' | 'service' | 'license' | 'nda' | 'msa' | 'sow'
  | 'insurance' | 'employment' | 'partnership' | 'loan'
  | 'retainer' | 'engagement_letter' | 'settlement' | 'other';

export type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated' | 'renewed';
export type ContractValueFrequency = 'one_time' | 'monthly' | 'quarterly' | 'annual';
export type RenewalUrgency = 'expired' | 'urgent' | 'upcoming' | 'watch' | 'future';

export interface Contract {
  id: string;
  entity_id: string;
  contract_type: ContractType;
  title: string;
  counterparty_name: string;
  counterparty_contact: string | null;
  description: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  auto_renew: boolean;
  renewal_notice_days: number;
  notice_deadline: string | null;
  value_cents: string | null;
  value_frequency: ContractValueFrequency | null;
  currency: string;
  s3_path: string | null;
  status: ContractStatus;
  replaced_by_id: string | null;
  linked_record_type: string | null;
  linked_record_id: string | null;
  tags: string[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Document intelligence (populated async by nightly batch processor)
  extracted_text?: string | null;
  text_extracted_at?: string | null;
  ai_summary?: string | null;
  ai_summary_generated_at?: string | null;
  ai_extracted_fields?: Record<string, unknown> | null;
  // camelCase payload shape for create/update
  contractType?: ContractType;
  counterpartyName?: string;
  counterpartyContact?: string | null;
  effectiveDate?: string | null;
  expiryDate?: string | null;
  autoRenew?: boolean;
  renewalNoticeDays?: number;
  valueCents?: number | null;
  valueFrequency?: ContractValueFrequency | null;
}

export interface ContractWithUrgency extends Contract {
  days_until_expiry: number | null;
  urgency: RenewalUrgency | null;
}

// Alias so api.ts callers can refer to "ContractRow" generically.
export type ContractRow = Contract;

// NS4 R9 — contract amendment chain (mig 074).
export interface ContractChainEntry {
  id: string;
  parent_contract_id: string | null;
  amendment_number: number;
  title: string;
  status: ContractStatus;
  effective_date: string | null;
  expiry_date: string | null;
  supersedes_at: string | null;
  created_at: string;
  value_cents: string | null;
  value_frequency: ContractValueFrequency | null;
}

export interface ContractAmendInput {
  title?: string;
  description?: string;
  effectiveDate?: string;
  expiryDate?: string | null;
  valueCents?: number;
  valueFrequency?: ContractValueFrequency;
  counterpartyName?: string;
  counterpartyContact?: string;
  contractType?: ContractType;
  s3Path?: string;
}

// NS4 R9 — contract signatures (mig 074).
export interface ContractSignatureRow {
  id: string;
  signer_user_id: string | null;
  signer_name: string;
  signer_email: string;
  signer_role: string | null;
  typed_signature: string;
  signed_content_sha256: string;
  signed_at: string;
  signer_ip: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
}

export interface ContractSignInput {
  typedSignature: string;
  signerName: string;
  signerEmail: string;
  signerRole?: string;
}

export type DocumentSourceType =
  | 'policy' | 'contract' | 'vendor_agreement'
  | 'contractor_agreement' | 'continuity_document' | 'inbound';

export interface UnifiedDocument {
  id: string;
  entity_id: string;
  source_type: DocumentSourceType;
  title: string;
  category: string;
  counterparty: string | null;
  s3_path: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  // Added by migration 032 — search-targetable narrative field from each
  // source (contract.description, vendor_agreement.notes, etc.)
  description: string | null;
}

export interface DocumentRenewal extends UnifiedDocument {
  days_until_expiry: number;
  urgency: RenewalUrgency;
}

// Inbound email (Phase 1, 2026-06-14)
export type InboundLane = 'filed' | 'needs_review' | 'quarantined';
export interface InboundMessage {
  id: string;
  purpose: string | null;
  sender: string | null;
  subject: string | null;
  provenanceTier: 'verified' | 'partial' | 'unverified' | 'internal';
  status: 'received' | 'processing' | 'filed' | 'needs_review' | 'quarantined' | 'failed' | 'dropped';
  classification: string | null;
  confidence: number | null;
  attachmentCount: number;
  receivedAt: string;
  processedAt: string | null;
  acknowledgedAt: string | null;
  error: string | null;
}
export interface InboundRoute {
  id: string;
  purpose: string;
  address: string;
  status: 'pending' | 'active' | 'retired';
  allowedSenders: string[];
  createdAt: string;
}
export interface InboundHandleAddress { purpose: string; address: string; }
export interface InboundHandleStatus {
  handle: string;
  valid: boolean;
  available: boolean;
  reason: string | null;
  message: string;
  addresses: InboundHandleAddress[];
  suggestions: string[];
}
export interface RegistryDocument {
  id: string;
  s3Key: string;
  filename: string | null;
  docType: string | null;
  category: string;
  sourceVector: string;
  sourceTable: string | null;
  sourceId: string | null;
  inboundMessageId: string | null;
  provenanceTier: string;
  createdAt: string;
}

export interface PolicyDocument {
  id: string;
  entity_id: string;
  doc_type: string;
  doc_name: string;
  title: string | null;
  version: string;
  template_id: string | null;
  status: string;
  effective_date: string | null;
  signed_date: string | null;
  signing_authority: string | null;
  is_active: boolean;
  content?: string | null;
  s3_path?: string | null;
  created_at: string;
  updated_at: string;
  // NS4 followup R6 — counsel-review attestation
  counsel_reviewed_at: string | null;
  counsel_reviewer_name: string | null;
  counsel_review_role: 'attorney' | 'cpa' | 'benefits_attorney' | 'other' | null;
  counsel_review_note: string | null;
  next_review_due_at: string | null;
}

export interface PolicyCounselReviewPayload {
  reviewerName: string;
  reviewerRole?: 'attorney' | 'cpa' | 'benefits_attorney' | 'other';
  note?: string;
  reviewedAt?: string;
  nextReviewDueAt?: string;
}

// NS4 R7 — fixed-asset register (mig 073).
export type FixedAssetClass = 'computer' | 'furniture' | 'equipment' | 'vehicle' | 'real_property' | 'leasehold_improvement' | 'intangible' | 'other';
export type FixedAssetMethod = 'straight_line' | 'macrs' | 'section_179' | 'bonus_depreciation' | 'none';
export interface FixedAsset {
  id: string;
  entity_id: string;
  asset_name: string;
  description: string | null;
  asset_class: FixedAssetClass;
  cost: string;
  salvage_value: string;
  acquired_date: string;
  placed_in_service_date: string;
  useful_life_years: string;
  depreciation_method: FixedAssetMethod;
  irc_class_life: string | null;
  section_179_election: boolean;
  /**
   * Per-asset §179 election amount in cents (P1.1 — IRC §179 enforcement).
   * NULL = elect full asset cost (back-compat with section_179_election=true).
   * Validated against entity-year limits at Schedule M-1 time.
   */
  section_179_amount_cents: number | null;
  bonus_depreciation_election: boolean;
  disposed_date: string | null;
  disposal_proceeds: string | null;
  disposal_method: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /**
   * W2-18: server-computed, income-capped depreciation for the requested year.
   * Present when listFixedAssets could compute it; the register prefers these
   * numbers so it matches Schedule M-1 exactly (same IRC §179(b)(3) income cap).
   * Absent → register falls back to its client-side math.
   */
  depreciation?: {
    year: number;
    taxYtd: number;            // this year's depreciation (dollars)
    taxBook: number;           // book value after this year (dollars)
    taxAccum: number;          // accumulated depreciation (dollars)
    section179ElectedCents: number | null;
    section179AllowedCents: number | null;
    section179IncomeCapped: boolean; // §179 reduced below elected by the income limit → carried forward
  };
}
export interface FixedAssetInput {
  assetName: string;
  description?: string;
  assetClass?: FixedAssetClass;
  cost: number;
  salvageValue?: number;
  acquiredDate: string;
  placedInServiceDate?: string;
  usefulLifeYears: number;
  depreciationMethod?: FixedAssetMethod;
  ircClassLife?: number;
  section179Election?: boolean;
  /**
   * Optional partial §179 election amount in cents. NULL/undefined = full asset cost.
   */
  section179AmountCents?: number | null;
  bonusDepreciationElection?: boolean;
  notes?: string;
  disposedDate?: string | null;
  disposalProceeds?: number | null;
  disposalMethod?: string | null;
}

// NS4 R7 — compliance_rule_facts global registry (mig 067).
export interface ComplianceRuleFact {
  id: string;
  fact_key: string;
  title: string;
  summary: string;
  applies_when: Record<string, unknown>;
  suppress_keywords: string[];
  citation_url: string | null;
  source_authority: string | null;
  effective_date: string | null;
  retired_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// --- Workflow approvals (Phase 2f, migration 031) ---
// Round-16 audit P1 fix: this union previously omitted 'time_entry' and
// 'pay_rule', but the approvals engine DOES create rows of both types
// (time_entry via the seeded time-entry rule; pay_rule via the pay-rule
// approval flow), and the backend returns them unfiltered. Because the
// union lied, SUBJECT_LABELS[subject_type] destructures typechecked as
// always-defined and crashed the /workflows page at runtime on any such
// row. The union now matches what the DB actually emits, which forces
// SUBJECT_LABELS to be exhaustive (tsc-enforced).
export type ApprovalSubjectTypeKey =
  | 'expense' | 'bill' | 'pto_request' | 'contract'
  | 'invoice' | 'contractor_payment' | 'policy_document'
  | 'time_entry' | 'pay_rule';
export type ApprovalTriggerTypeKey = 'amount_gte' | 'duration_gte' | 'always';
// Comprehensive-sweep audit fix: 'manager' was missing even though
// migration 138 seeds every entity's pto_request AND time_entry approval
// rules with approver_role='manager', and both the backend
// (approvals-engine.js APPROVER_ROLES) and this file's own sibling module
// (src/lib/approvals.ts APPROVER_ROLES) already list it — this narrower
// type was the one place in the frontend that lagged behind, making the
// seeded rules unreconstructable via the New Rule form (see
// Workflows.tsx's Required-approver dropdown).
export type ApproverRoleKey = 'owner' | 'admin' | 'bookkeeper' | 'manager';
export type ApprovalStatusKey = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface ApprovalRuleRow {
  id: string;
  entity_id: string;
  name: string;
  description: string | null;
  subject_type: ApprovalSubjectTypeKey;
  trigger_type: ApprovalTriggerTypeKey;
  threshold_cents: string | number | null;
  threshold_days: number | null;
  approver_role: ApproverRoleKey;
  filter_json: Record<string, unknown>;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRuleCreatePayload {
  name: string;
  description?: string | null;
  subjectType: ApprovalSubjectTypeKey;
  triggerType: ApprovalTriggerTypeKey;
  thresholdCents?: number | null;
  thresholdDays?: number | null;
  approverRole: ApproverRoleKey;
  filterJson?: Record<string, unknown>;
  isActive?: boolean;
}

export interface ApprovalRow {
  id: string;
  entity_id: string;
  subject_type: ApprovalSubjectTypeKey;
  subject_id: string;
  subject_summary: string | null;
  requested_by: string | null;
  required_role: ApproverRoleKey;
  rule_id: string | null;
  status: ApprovalStatusKey;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  amount_cents: string | number | null;
  subject_date: string | null;
  created_at: string;
  updated_at: string;
  rule_name?: string | null;
  rule_description?: string | null;
}

export interface PendingApprovalRow {
  id: string;
  entity_id: string;
  subject_type: ApprovalSubjectTypeKey;
  subject_id: string;
  subject_summary: string | null;
  amount_cents: string | number | null;
  subject_date: string | null;
  required_role: ApproverRoleKey;
  requested_by: string | null;
  rule_name: string | null;
  rule_description: string | null;
  requested_by_email: string | null;
  created_at: string;
}

// --- Billing (Stripe) ---
export type SubscriptionTierKey = 'starter' | 'professional' | 'enterprise';
export type StripeSubscriptionStatus =
  | 'trialing' | 'active' | 'past_due' | 'canceled'
  | 'incomplete' | 'incomplete_expired' | 'unpaid' | 'paused'
  | null;

export interface BillingSubscriptionResponse {
  tier: SubscriptionTierKey;
  planName: string;
  status: StripeSubscriptionStatus;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasSubscription: boolean;
  hasStripeCustomer: boolean;
  maxEntities: number;
}

export interface BillingPlan {
  tier: SubscriptionTierKey;
  name: string;
  price: number;
  priceLabel: string;
  popular?: boolean;
  priceIds: { monthly?: string; annual?: string };
  limits: { maxEntities: number; monthlyAiBudgetCents: number; monthlyExpenses: number };
  features: string[];
}

// B3 (NS20, 2026-06-12) — AI top-up packs. Canonical values live in
// shared-layer/topup-packs.js; landing-copy-accuracy.test.ts pins any
// frontend literal against that registry.
export type TopupPackKey = 'small' | 'standard' | 'large';

export interface TopupPack {
  key: TopupPackKey;
  name: string;
  priceCents: number;
  creditCents: number;
}

export interface TopupPacksResponse {
  packs: TopupPack[];
  expiryDays: number;
  activeCredits: Array<{
    pack_key: TopupPackKey;
    credit_cents: number;
    purchased_at: string;
    expires_at: string;
  }>;
  activeCreditCents: number;
}

export type CredentialType =
  | 'professional_license' | 'certification' | 'bar_admission'
  | 'registration' | 'membership' | 'specialty_board' | 'compact_privilege';

export type ProfessionCategory =
  | 'medical' | 'legal' | 'accounting' | 'engineering' | 'architecture'
  | 'financial_services' | 'real_estate' | 'education' | 'trades'
  | 'clinical_non_physician' | 'other';

export type CredentialStatus = 'active' | 'pending_renewal' | 'expired' | 'suspended' | 'inactive';

export interface Credential {
  id: string;
  entity_id: string;
  user_id: string;
  credential_type: CredentialType;
  profession_category: ProfessionCategory;
  name: string;
  issuing_body: string;
  credential_number?: string | null;
  jurisdiction?: string | null;
  issue_date?: string | null;
  expiration_date?: string | null;
  renewal_period_years?: number | null;
  status: CredentialStatus;
  compact_membership?: string | null;
  compact_privilege_states?: string[] | null;
  is_primary_state?: boolean;
  ce_hours_required?: number | null;
  ce_reporting_period?: string | null;
  document_s3_path?: string | null;
  verification_url?: string | null;
  notes?: string | null;
  replaced_by_id?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface CredentialDetail extends Credential {
  ce_hours_earned?: number;
  ce_activities_logged?: number;
}

export interface CEEntry {
  id: string;
  entity_id: string;
  user_id: string;
  credential_id?: string | null;
  activity_date: string;
  provider: string;
  title: string;
  category?: string | null;
  hours: number;
  reporting_period?: string | null;
  cost?: number | null;
  related_expense_id?: string | null;
  certificate_s3_path?: string | null;
  verification_code?: string | null;
  notes?: string | null;
  created_at: string;
}

export type TimeEntryStatus = 'draft' | 'attested' | 'paid' | 'reversed';
export type TimeEntryWorkType =
  // Existing (pre-Preset-Phase-1, preserve for back-compat)
  | 'clinical_shift' | 'call' | 'consulting' | 'legal_matter' | 'admin' | 'travel' | 'other'
  // Preset Phase 1 (2026-05-20): generic across professions
  | 'billable_hour' | 'project_work' | 'retainer' | 'commission_event' | 'per_unit'
  // Preset Phase 1: profession-specific additions
  | 'production_day'    // dentist
  | 'showing'           // realtor (V2)
  | 'closing_prep'      // realtor (V2)
  | 'on_call'           // physician (V2)
  | 'procedure_day'     // physician_ir (V2)
  | 'OR_block';         // physician surgery (V2)
export type TimeEntryRateType = 'daily' | 'hourly' | 'flat' | 'unpaid';
export type TimeEntrySource = 'manual' | 'voice' | 'inferred_expense' | 'inferred_calendar' | 'agency_timesheet_ocr' | 'mobile_clock' | 'schedule_import';

/** A parsed schedule block returned by /ingest/schedule, pre-commit (SCHED-1 Phase 2). */
export interface ScheduleParsedEntry {
  workDate: string;
  workType: string;
  startTime: string | null;
  endTime: string | null;
  hoursWorked: number | null;
  spanning: boolean;
  status: 'worked' | 'scheduled';
  facility: string | null;
  location: string | null;
  client: string | null;
  agency: string | null;
}

export interface TimeEntry {
  id: string;
  entity_id: string;
  user_id: string;
  work_date: string;
  facility_name?: string | null;
  location?: string | null;
  geo_lat?: number | null;
  geo_lng?: number | null;
  hours_worked?: number | null;
  daily_rate?: number | null;
  start_time?: string | null;
  end_time?: string | null;
  break_minutes?: number | null;
  rate_type: TimeEntryRateType;
  rate_multiplier: number;
  gross_pay?: number | null;
  work_type: TimeEntryWorkType;
  source: TimeEntrySource;
  description?: string | null;
  project_code?: string | null;
  matter_or_client_ref?: string | null;
  billable: boolean;
  status: TimeEntryStatus;
  attested_at?: string | null;
  attested_by?: string | null;
  payroll_export_id?: string | null;
  payload_hash?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at?: string | null;
}

/** Fetch with AbortController timeout */
async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Make an authenticated API request.
 * Automatically adds Authorization and X-Entity-Id headers.
 * On 401, retries once with a forced token refresh before failing.
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  if (isLocalMode) {
    throw new Error(`API not configured — running in local mode. Set VITE_API_BASE_URL to connect.`);
  }

  const { method = 'GET', body, entityId, skipAuth, skipEntityHeader, timeoutMs = API_TIMEOUT_MS } = options;
  const activeEntityId = skipEntityHeader ? null : (entityId || useAppStore.getState().activeEntity?.id);

  // Check online status
  if (!navigator.onLine) {
    throw new NetworkError('You are offline. Changes will be queued and synced when connected.', true);
  }

  const doRequest = async (forceRefresh: boolean): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (!skipAuth) {
      // If forceRefresh, clear in-memory token cache to force a refresh
      const token = await getAccessToken(forceRefresh);
      if (!token) {
        throw httpToAppError(401);
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (activeEntityId) {
      headers['X-Entity-Id'] = activeEntityId;
    }

    // S28: inject the admin step-up token from sessionStorage on /admin/*
    // requests. Backend's ensurePlatformAdminMfa requires it on every
    // gated write. The /admin/step-up endpoints themselves don't need it
    // (they MINT it), so we exclude them. Step-up token is per-tab and
    // clears when the tab closes (sessionStorage, NOT localStorage).
    if (typeof window !== 'undefined' && path.startsWith('/admin/') && !path.startsWith('/admin/step-up')) {
      try {
        const stepUpToken = window.sessionStorage.getItem('nexus_admin_step_up');
        if (stepUpToken) headers['X-Admin-Step-Up'] = stepUpToken;
      } catch { /* sessionStorage blocked — admin write will 401 with STEP_UP_REQUIRED */ }
    }

    const url = `${API_BASE}${path}`;
    return fetchWithTimeout(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, timeoutMs);
  };

  try {
    let response = await doRequest(false);

    // On 401, decide whether it's "session dead" or "step-up needed".
    // Admin step-up failures must NOT sign the user out — they just
    // need to re-prove TOTP via the AdminStepUpDialog modal.
    if (response.status === 401 && !skipAuth) {
      // Peek at the response body BEFORE the retry — admin step-up 401s
      // come from the platform Lambda directly, so they look the same
      // to API Gateway as auth failures. The body distinguishes.
      const peekText = await response.clone().text().catch(() => '');
      const isAdminStepUp =
        path.startsWith('/admin/') &&
        /STEP_UP_REQUIRED|step.{0,3}up|Re-authenticate via the admin/i.test(peekText);
      if (isAdminStepUp) {
        // Notify AdminLayout to re-show the step-up dialog. Don't retry,
        // don't sign out — let the dialog mint a new token.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('nexus:admin-step-up-expired'));
        }
        throw httpToAppError(response.status, peekText);
      }

      response = await doRequest(true);

      // Still 401 after refresh — session is dead, redirect (with loop protection)
      if (response.status === 401) {
        signOut();
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        throw httpToAppError(401);
      }
    }

    if (!response.ok) {
      const errorBody = await response.text();
      // Distinguish a real server error from a service-worker offline fallback
      // (the SW returns 503 with X-Nexus-SW-Fallback:1 when the actual fetch
      // fails client-side — DNS, CORS, or Aurora cold-start). Surface a
      // network-specific message rather than "the server is broken".
      if (response.status === 503 && response.headers.get('X-Nexus-SW-Fallback') === '1') {
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        throw new NetworkError(
          isOffline
            ? `You appear to be offline. This request (${path}) will retry when you reconnect.`
            : `Could not reach the server for ${path}. Retry, and if it persists check your connection.`,
          isOffline,
        );
      }
      throw httpToAppError(response.status, errorBody);
    }

    return response.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new NetworkError(`Request timed out after ${timeoutMs / 1000}s (${path}). Please try again.`);
    }
    // fetch throws TypeError for genuine network failures AND for CORS-blocked
    // responses (e.g. a 401 from an authorizer that doesn't emit CORS headers).
    // We can't distinguish them from JS, so we tell the user both possibilities.
    if (err instanceof TypeError) {
      const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      const onlineHint = isOffline
        ? 'You appear to be offline.'
        : 'Your device reports it is online, so this is most likely an authentication or CORS problem (not a true network failure). Try signing out and signing in again.';
      const msg = `Could not complete request to ${path}. ${onlineHint}`;
      console.error('[api] fetch TypeError', { path, url: `${API_BASE}${path}`, err });
      throw new NetworkError(msg, isOffline);
    }
    throw err;
  }
}

// --- Convenience methods ---

// 2026-06-16 Onboarding Wave 1 — declared compliance profile (source of truth).
// Tri-state values; 'unknown' is the unanswered default that never suppresses a
// real obligation. Persisted by compliance-agent updateComplianceProfile (mig 107/108).
export interface ComplianceProfile {
  payroll_model: 'self' | 'external_provider' | 'none' | 'unknown';
  payroll_provider_name: string | null;
  pays_1099_contractors: 'yes' | 'no' | 'unknown';
  owner_takes_w2: 'yes' | 'no' | 'planned' | 'unknown';
  member_count: 'one' | 'multiple' | 'unknown';
  // Wave 3 (mig 109): has_foreign_accounts='yes' → FBAR reminder; wants_ptet='decline'
  // → suppress the PTET (SALT-cap workaround) reminders.
  has_foreign_accounts: 'yes' | 'no' | 'unknown';
  wants_ptet: 'want' | 'decline' | 'undecided' | 'unknown';
  // Wave 4 (mig 115): has_employees → Form 941/940 ahead of data; operating_status
  // 'not_yet' suppresses estimated-tax; multi_state_work + prior_filings_status
  // each add an advisory. Tri-state — 'unknown' never changes behavior.
  has_employees: 'yes' | 'no' | 'unknown';
  operating_status: 'operating' | 'not_yet' | 'unknown';
  multi_state_work: 'yes' | 'no' | 'unknown';
  prior_filings_status: 'current' | 'behind' | 'unknown';
  updated_at: string | null;
}

// Phase 8 — reconstruction memo types.
export type CcMemoCitationReason = 'never_kept_lost' | 'casualty_fire_flood' | 'possible_theft_review';
export interface CcMemoInput {
  attendees?: string;
  businessPurpose?: string;
  ircCitation?: string;
  citationReason?: CcMemoCitationReason;
}
export interface CcMemo {
  id: string;
  cc_statement_line_item_id: string;
  attendees: string | null;
  business_purpose: string | null;
  irc_citation: string | null;
  citation_reason: CcMemoCitationReason | null;
  drafted_by_user_id: string | null;
  drafter_name: string | null;
  countersigned_by_user_id: string | null;
  countersigner_name: string | null;
  countersigner_role: string | null;
  countersigned_at: string | null;
  finalized_at: string | null;
  frozen_sha256: string | null;
  amendment_number: number;
  status: 'draft' | 'finalized' | 'superseded' | 'void';
  created_at: string;
  updated_at: string;
}

// Phase 9 — a statement line as seen through the cardholder-routing surface.
export interface CcCardholderLine {
  id: string;
  txn_date: string | null;
  amount: number;
  description: string | null;
  card_last4: string | null;
  cardholder_name?: string | null;
  cardholder_resolution?: 'unresolved' | 'auto_last4' | 'auto_name' | 'manual';
  resolved_employee_id?: string | null;
  match_status: string;
  personal_reason: string | null;
  first_name?: string | null;   // review surface only (resolved employee)
  last_name?: string | null;
  issuer: string | null;
  period_start: string | null;
  period_end: string | null;
}

export const api = {
  // Pre-launch demand capture from the landing-page demo button.
  // Unauthenticated endpoint — sends no entity header, does not need
  // a Cognito token.
  submitDemoIntake: (data: { email: string; profession: string; phone?: string; useCase?: string }) =>
    apiRequest<{ ok: true }>('/demo/intake', {
      method: 'POST',
      body: data as Record<string, unknown>,
      skipEntityHeader: true,
      skipAuth: true,
    }),

  // Account lifecycle (CCPA/GDPR account deletion)
  deleteAccount: (confirmPhrase: string) =>
    apiRequest<{ success: boolean; entitiesDeleted: number }>('/me/account', {
      method: 'DELETE',
      body: { confirmPhrase },
    }),

  // Billing (Stripe). Org-scoped, not entity-scoped — getBillingSubscription
  // returns the subscription state shared across every entity in the org.
  // checkout/portal endpoints return a hosted Stripe URL the caller should
  // redirect the browser to.
  getBillingSubscription: () =>
    apiRequest<BillingSubscriptionResponse>('/billing/subscription', { skipEntityHeader: true }),
  // /billing/plans is behind the Cognito authorizer; /pricing is internal-only
  // (authed AppShell), so callers always have a token. If we later wire a
  // public landing page, make the route truly public on API Gateway first.
  listBillingPlans: () =>
    apiRequest<{ plans: BillingPlan[] }>('/billing/plans', { skipEntityHeader: true }),

  // Public version metadata. Stamped by scripts/deploy.cjs at deploy time
  // and read from /opt/nodejs/version in the supervisor Lambda. Returns
  // both the internal build ID (for rollback / support correlation) and
  // the external marketing version (for customer-facing surfaces).
  getVersion: () =>
    apiRequest<{
      version: string;
      gitSha: string;
      deployedAt: string | null;
      runtime: string;
      marketing: {
        family: string;
        product: string;
        version: string;
        codename: string;
        releaseStage: 'alpha' | 'beta' | 'rc' | 'stable';
        releaseDate: string;
      } | null;
    }>(
      '/version',
      { skipAuth: true, skipEntityHeader: true },
    ),

  // S-0B per-tenant data export. Owner/admin role only. Rate-limited to
  // 1 export per hour per entity. Returns a 15-minute presigned S3 URL
  // to a zip containing every tenant-scoped table + every owned file +
  // MANIFEST.json + README.txt.
  exportTenantData: () =>
    apiRequest<{
      downloadUrl: string;
      expiresInSeconds: number;
      fileName: string;
      bytes: number;
      sha256: string;
      rowCount: number;
      tableCount: number;
      fileCount: number;
      generatedAt: string;
    }>('/me/export'),

  // S-6 CPA-ready monthly package. Owner/admin role. Rate-limited to 20/day
  // per entity. Returns a 15-minute presigned S3 URL to a zip containing
  // transactions.csv + audit-log.csv + open-items.csv + pl-summary.pdf +
  // receipts/* + README.txt (with version stamp + SHA256 hashes).
  generateMonthlyPackage: (year: number, month: number) =>
    apiRequest<{
      downloadUrl: string;
      expiresInSeconds: number;
      fileName: string;
      bytes: number;
      sha256: string;
      period: { year: number; month: number; start: string; end: string };
      counts: {
        transactions: number;
        auditEntries: number;
        openItems: number;
        receipts: number;
      };
      pl: {
        monthIncome: number;
        monthExpenseTotal: number;
        monthWages: number;
        monthNet: number;
        ytdIncome: number;
        ytdExpenseTotal: number;
        ytdWages: number;
        ytdNet: number;
      };
      generatedAt: string;
    }>(`/reports/monthly-package?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`),

  // PP4 — One-click CPA Handoff Workpaper (full tax year).
  generateCpaHandoff: (year: number, includeReceipts = false) =>
    apiRequest<{
      downloadUrl: string;
      expiresInSeconds: number;
      fileName: string;
      bytes: number;
      sha256: string;
      year: number;
      formArtifact: string;
      totals: { grossReceipts: number; totalDeductions: number; ordinaryIncome: number };
      counts: { transactions: number; contractors: number; receipts: number };
      sections: { taxProjection: string; compMemo: string; scheduleM1: string };
      journalBalanced: boolean;
      generatedAt: string;
    }>(`/reports/cpa-handoff?year=${encodeURIComponent(year)}${includeReceipts ? '&includeReceipts=true' : ''}`),

  // NS4 R7 — Schedule M-1 reconciliation MVP (book vs tax net income).
  getScheduleM1: (year?: number) => {
    const qs = year ? `?year=${year}` : '';
    return apiRequest<{
      year: number;
      bookNetIncome: number;
      taxNetIncome: number;
      lines: Array<{ line: number | string; label: string; amount: number; sign: '+' | '-' | '='; note?: string }>;
      detail: {
        bookDepreciation: number;
        taxDepreciation: number;
        mealsBookAmount: number;
        totalIncome: number;
        totalExpense: number;
        totalWages: number;
        totalSection105: number;
      };
      warnings: string[];
      mvpDisclaimer: string;
      formArtifact: string; // W2-21: "Form 1120 Schedule M-1" | "Form 1120-S …" | "Form 1065 …" per tax_election
    }>(`/reports/schedule-m1${qs}`);
  },

  // NS4 R7 — Journal-entry export (QBO / Xero / IIF). Owner/admin only.
  // Date range optional; defaults to current month.
  generateJournalExport: (params?: { start?: string; end?: string }) => {
    const qs = params && (params.start || params.end)
      ? '?' + new URLSearchParams({
          ...(params.start ? { start: params.start } : {}),
          ...(params.end ? { end: params.end } : {}),
        }).toString()
      : '';
    return apiRequest<{
      range: { start: string; end: string };
      entries: number;
      unmappedCount?: number;
      balance?: { balanced: boolean; drift: number; totalDebit: number; totalCredit: number };
      iif?: { url: string; expiresInSeconds: number };
      csv?: { url: string; expiresInSeconds: number };
      message?: string;
    }>(`/reports/journal-export${qs}`);
  },

  createBillingCheckout: (data: { tier: 'professional' | 'enterprise'; interval?: 'monthly' | 'annual'; origin?: string }) =>
    apiRequest<{ url: string; id: string }>('/billing/checkout', {
      method: 'POST',
      body: data as Record<string, unknown>,
      skipEntityHeader: true,
    }),
  createBillingPortal: (data: { origin?: string } = {}) =>
    apiRequest<{ url: string }>('/billing/portal', {
      method: 'POST',
      body: data as Record<string, unknown>,
      skipEntityHeader: true,
    }),

  // B3: AI top-up packs. ENTITY-scoped (the credit raises the active
  // entity's AI cap), unlike the org-scoped subscription endpoints above.
  getTopupPacks: () =>
    apiRequest<TopupPacksResponse>('/billing/topup-packs'),
  createTopupCheckout: (data: { packKey: TopupPackKey; origin?: string }) =>
    apiRequest<{ url: string; id: string }>('/billing/topup-checkout', {
      method: 'POST',
      body: data as Record<string, unknown>,
    }),

  // Credentials — licenses, certifications, bar admissions, memberships
  listCredentials: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ credentials: Credential[] }>(`/credentials${qs}`);
  },
  getCredential: (id: string) =>
    apiRequest<{ credential: CredentialDetail }>(`/credentials/${id}`),
  createCredential: (data: Partial<Credential>) =>
    apiRequest<{ credential: Credential }>('/credentials', { method: 'POST', body: data as Record<string, unknown> }),
  updateCredential: (id: string, data: Partial<Credential>) =>
    apiRequest<{ credential: Credential }>(`/credentials/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deleteCredential: (id: string) =>
    apiRequest<{ deleted: string }>(`/credentials/${id}`, { method: 'DELETE' }),

  // Renew credential — creates a new credential row based on the old one,
  // links old→new via replaced_by_id. Body can override issueDate,
  // expirationDate, credentialNumber, notes.
  renewCredential: (id: string, data: {
    issueDate?: string;
    expirationDate?: string;
    credentialNumber?: string;
    notes?: string;
  }) =>
    apiRequest<{
      credential: Credential;
      replaced: { id: string; replaced_by_id: string };
    }>(`/credentials/${id}/renew`, { method: 'POST', body: data as Record<string, unknown> }),

  // Continuing education
  listCE: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ entries: CEEntry[] }>(`/continuing-education${qs}`);
  },
  createCE: (data: Partial<CEEntry> & { activityDate: string; provider: string; title: string; hours: number }) =>
    apiRequest<{ entry: CEEntry }>('/continuing-education', { method: 'POST', body: data as Record<string, unknown> }),
  deleteCE: (id: string) =>
    apiRequest<{ deleted: string }>(`/continuing-education/${id}`, { method: 'DELETE' }),

  // Time entries (industry-general: consulting hours, billable matter time,
  // clinical shifts, admin, travel). State machine: draft → attested → paid,
  // with reversed as a terminal alt. Only drafts are mutable.
  listTimeEntries: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ entries: TimeEntry[] }>(`/time-entries${qs}`);
  },
  createTimeEntry: (data: Record<string, unknown> & { workDate: string }) =>
    apiRequest<{ entry: TimeEntry }>('/time-entries', { method: 'POST', body: data }),
  updateTimeEntry: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ entry: TimeEntry }>(`/time-entries/${id}`, { method: 'PATCH', body: data }),
  attestTimeEntry: (id: string) =>
    apiRequest<{ entry: TimeEntry }>(`/time-entries/${id}/attest`, { method: 'POST' }),
  bulkAttestTimeEntries: (ids: string[]) =>
    apiRequest<{ attested: number; total: number; results: { id: string; ok: boolean; status: number; error: string | null }[] }>(
      '/time-entries/bulk-attest', { method: 'POST', body: { ids } }),
  // SCHED-1 Phase 2: parse a pasted/uploaded schedule into reviewable blocks (no writes).
  // Gap #5 (2026-07-14): a PDF upload is async — omit jobId to START (may return
  // status:'IN_PROGRESS' + jobId instead of entries), then re-call with jobId to
  // POLL (mirrors api.ingestVoice's start/poll shape). Images/text/CSV are
  // unaffected — always return entries synchronously in one call.
  parseSchedule: (data: { text?: string; s3Key?: string; today: string; preset?: string; jobId?: string }) =>
    apiRequest<{ entries?: ScheduleParsedEntry[]; meta?: Record<string, string>; lineCount?: number; status?: string; jobId?: string; s3Key?: string }>(
      '/ingest/schedule', { method: 'POST', body: data }),
  // SCHED-1 Phase 2: commit reviewed schedule entries as draft time entries.
  importScheduleEntries: (entries: ScheduleParsedEntry[], opts?: { attestWorked?: boolean; today?: string }) =>
    apiRequest<{ created: number; attested: number; skipped: number; total: number }>(
      '/time-entries/import', { method: 'POST', body: { entries, ...(opts || {}) } }),
  reverseTimeEntry: (id: string, reason: string) =>
    apiRequest<{ entry: TimeEntry }>(`/time-entries/${id}/reverse`, { method: 'POST', body: { reason } }),
  deleteTimeEntry: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/time-entries/${id}`, { method: 'DELETE' }),

  // Expenses
  createExpense: (data: Record<string, unknown>) =>
    apiRequest('/expenses', { method: 'POST', body: data }),

  listExpenses: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/expenses${qs}`);
  },

  getExpense: (id: string) =>
    apiRequest(`/expenses/${id}`),

  updateExpense: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/expenses/${id}`, { method: 'PATCH', body: data }),

  // Ingestion
  // 2026-06-13 upload-403 fix: mint a presigned PUT URL so the browser can
  // upload the file straight to S3 (around CloudFront/WAF's 8KB body limit).
  // Tiny request body → passes WAF. Returns { uploadUrl, s3Key }.
  getUploadUrl: (data: { contentType: string; ccCrop?: boolean; ccCropGroup?: string }) =>
    apiRequest<{ uploadUrl: string; s3Key: string; expiresIn: number }>(
      '/ingest/upload-url', { method: 'POST', body: data }),

  ingestCamera: (data: { imageBase64?: string; s3Key?: string }) =>
    apiRequest('/ingest/camera', { method: 'POST', body: data }),

  ingestVoice: (data: { audioS3Key?: string; audioBase64?: string; transcript?: string; format?: string; transcriptionJobName?: string }) =>
    apiRequest('/ingest/voice', { method: 'POST', body: data }),

  // Faster voice (2026-06-19): soft per-tenant gate + usage accounting for
  // browser-direct AWS Transcribe streaming. preflight is advisory (the UI honors
  // it); usage logs streamed seconds toward the monthly minute cap.
  voicePreflight: () =>
    apiRequest<{ allowed: boolean; minutesUsed: number; minutesLimit: number; reason: string | null }>(
      '/ingest/voice/preflight', { method: 'POST', body: {} }),

  logVoiceUsage: (seconds: number) =>
    apiRequest<{ ok: boolean; seconds: number }>(
      '/ingest/voice/usage', { method: 'POST', body: { seconds } }),

  ingestManual: (data: Record<string, unknown>) =>
    // Route to /expenses (supervisor direct createExpense) which actually persists.
    // /ingest/manual is a stub that only echoes input without INSERTing (see F-010).
    // Wrap response so callers still get {expense: {...}} shape.
    apiRequest<{ expense: Record<string, unknown> }>('/expenses', { method: 'POST', body: data })
      .then(res => ({ expense: (res as { expense?: Record<string, unknown> }).expense ?? res, compliancePassed: true })),

  // Smart Capture: universal doc triage. Accepts any of imageBase64, pdfBase64,
  // s3Key, or text. Server classifies (Claude Haiku) -> extracts fields ->
  // routes to the right table. Returns the doc type, extracted fields, and
  // a list of created records (or "candidate" markers for types whose write
  // path isn't yet automated, like W-4 -> employees pending Phase 1c).
  ingestSmart: (data: { imageBase64?: string; pdfBase64?: string; s3Key?: string; text?: string; suggestedType?: string }) =>
    apiRequest<{
      docType: string;
      classifierConfidence: number;
      extracted: Record<string, unknown>;
      created: Array<{
        kind: string;
        label: string;
        navigateTo: string | null;
        requiresUserConfirm?: boolean;
        ids: Record<string, string>;
        fields?: Record<string, unknown>;
      }>;
      ocrConfidence: number;
      s3Key?: string | null;
    }>('/ingest/smart', { method: 'POST', body: data as Record<string, unknown> }),

  // Compliance
  getComplianceStatus: () =>
    apiRequest('/compliance/status'),

  getComplianceCalendar: () =>
    apiRequest<{ events: Array<{ id?: string; title: string; dueDate: string; status?: string; source?: string; daysUntilDue?: number; category?: string }> }>('/compliance/calendar'),

  // S-8 compliance calendar auto-seed. Idempotent — safe to call repeatedly.
  // Populates entity-specific deadlines from the rule registry: Form 2553,
  // quarterly estimates, 1120-S, W-2/1099 filings, Solo 401(k), Section 105
  // review, state filings (NJ), and physician professional renewals if dates
  // are populated on the entity.
  refreshComplianceCalendar: () =>
    apiRequest<{
      entityId: string;
      generated: number;
      inserted: number;
      skipped: number;
      message: string;
    }>('/compliance/auto-seed', { method: 'POST' }),

  // 2026-06-16 Onboarding Wave 1: declared compliance profile (the operator's
  // source of truth). The compliance engine reads these FIRST — e.g. an explicit
  // pays_1099_contractors='no' suppresses the backup-withholding/Form-945 family.
  getComplianceProfile: () =>
    apiRequest<{ profile: ComplianceProfile }>('/compliance/profile'),
  updateComplianceProfile: (body: Partial<Omit<ComplianceProfile, 'updated_at'>>) =>
    apiRequest<{ profile: ComplianceProfile }>('/compliance/profile', {
      method: 'PATCH',
      body: body as Record<string, unknown>,
    }),

  // #5 (2026-06-19) — resolve a compliance-calendar item (close the loop).
  // action: 'resolve' (resolutionKind filed|paid|waived + date) | 'verify_posted'
  // (provider items) | 'reopen' | 'note'.
  resolveComplianceEvent: (
    eventId: string,
    body: { action: 'resolve' | 'verify_posted' | 'reopen' | 'note'; resolutionKind?: 'filed' | 'paid' | 'waived'; date?: string; note?: string },
  ) => apiRequest<{ item: Record<string, unknown> }>(`/compliance/calendar/${eventId}`, { method: 'PATCH', body }),

  // Attach an already-uploaded (presigned-PUT) file as proof for a calendar item.
  attachComplianceProof: (eventId: string, body: { s3Key: string; filename: string }) =>
    apiRequest<{ proof: { linkId: string; documentId: string; filename: string; createdAt: string } }>(
      `/compliance/calendar/${eventId}/proof`, { method: 'POST', body }),

  detachComplianceProof: (eventId: string, linkId: string) =>
    apiRequest<{ ok: boolean }>(`/compliance/calendar/${eventId}/proof?docId=${encodeURIComponent(linkId)}`, { method: 'DELETE' }),

  listComplianceProofs: (eventId: string) =>
    apiRequest<{ proofs: Array<{ link_id: string; document_registry_id: string; original_filename: string; s3_key: string; created_at: string }> }>(
      `/compliance/calendar/${eventId}/proof`),

  // S41: per-LLC AI compliance checklist (distinct from /compliance/calendar
  // which is date-anchored deadlines). Items are AI-generated from the
  // entity's profile and remain stable across regenerations via item_key.
  getComplianceChecklist: (params?: { includeArchived?: boolean; includeDismissed?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.includeArchived) qs.set('includeArchived', 'true');
    if (params?.includeDismissed === false) qs.set('includeDismissed', 'false');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest<{
      items: Array<{
        id: string;
        item_key: string;
        category: string;
        title: string;
        description: string | null;
        irc_citation: string | null;
        citation_url: string | null;
        frequency: string | null;
        due_pattern: string | null;
        priority: 'high' | 'medium' | 'low';
        applies_reason: string | null;
        status: 'open' | 'completed' | 'dismissed' | 'archived';
        completed_at: string | null;
        completion_note: string | null;
        dismissed_at: string | null;
        dismiss_reason: string | null;
        first_seen_in_checklist_id: string | null;
        last_seen_in_checklist_id: string | null;
        created_at: string;
        updated_at: string;
      }>;
      lastGeneration: {
        id: string;
        generated_at: string;
        generated_by_user_id: string | null;
        model_used: string | null;
        input_tokens: number | null;
        output_tokens: number | null;
        cost_cents: number | null;
        citation_version: string | null;
        items_proposed: number;
        items_new: number;
        items_archived_this_run: number;
      } | null;
      summary: {
        total: number;
        open: number;
        completed: number;
        dismissed: number;
        archived: number;
        high: number;
        medium: number;
        low: number;
        byCategory: Record<string, number>;
      };
      citationVersion: string;
    }>(`/compliance/checklist${suffix}`);
  },

  // POST /compliance/checklist/generate — AI regenerate; admin/owner only;
  // 5/day rate limit; budget-gated. Sonnet by default. ~5-15s wall time.
  generateComplianceChecklist: () =>
    apiRequest<{
      checklistId: string;
      generatedAt: string;
      itemsProposed: number;
      itemsNew: number;
      itemsArchived: number;
      model: string;
      tokens: { input: number; output: number };
      costCents: number;
      citationVersion: string;
    }>('/compliance/checklist/generate', { method: 'POST' }),

  updateComplianceChecklistItem: (itemId: string, action: 'complete' | 'dismiss' | 'reopen', note?: string) =>
    apiRequest<{
      item: { id: string; item_key: string; status: string; title: string };
      action: string;
    }>(`/compliance/checklist/items/${itemId}`, {
      method: 'PATCH',
      body: { action, note: note || '' } as Record<string, unknown>,
    }),

  // Rate cards (migration 040) — raw daily gross rates per work type.
  // Implied W-2 = raw × entity.w2PctOfGross / 100.
  listRateCards: () =>
    apiRequest<{
      rateCards: Array<{
        id: string;
        work_type: string;
        label: string;
        raw_daily_gross_cents: number;
        sort_order: number;
        is_active: boolean;
        rate_type: 'hourly' | 'daily' | 'salary_period' | 'per_unit';
        unit_label: string | null;
        applies_to_employee_ids: string[] | null;
        created_at: string;
        updated_at: string;
      }>;
    }>('/compensation/rate-cards'),

  createRateCard: (data: {
    workType: string; label: string; rawDailyGross: number; sortOrder?: number;
    rateType?: 'hourly' | 'daily' | 'salary_period' | 'per_unit';
    unitLabel?: string | null;
    appliesToEmployeeIds?: string[] | null;
  }) =>
    apiRequest<{ rateCard: { id: string; work_type: string; label: string; raw_daily_gross_cents: number; sort_order: number; is_active: boolean; rate_type: string; unit_label: string | null } }>(
      '/compensation/rate-cards', { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  updateRateCard: (id: string, data: {
    label?: string; rawDailyGross?: number; sortOrder?: number; isActive?: boolean;
    rateType?: 'hourly' | 'daily' | 'salary_period' | 'per_unit';
    unitLabel?: string | null;
    appliesToEmployeeIds?: string[] | null;
  }) =>
    apiRequest<{ rateCard: { id: string; work_type: string; label: string; raw_daily_gross_cents: number; sort_order: number; is_active: boolean; rate_type: string; unit_label: string | null } }>(
      `/compensation/rate-cards/${id}`, { method: 'PATCH', body: data as unknown as Record<string, unknown> }
    ),

  deleteRateCard: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/compensation/rate-cards/${id}`, { method: 'DELETE' }),

  // Pay Rules (S27 Phase 3.5 — migration 045). Auto-apply multipliers /
  // card swaps / flat adds to time entries based on date / day-of-week /
  // holiday / overtime / work_type / start-time. Pure matcher in
  // src/lib/pay-rules.ts.
  listPayRules: () =>
    apiRequest<{
      rules: Array<{
        id: string;
        name: string;
        description: string | null;
        trigger_type: string;
        trigger_config: Record<string, unknown>;
        action_type: string;
        action_config: Record<string, unknown>;
        applies_to_employee_ids: string[] | null;
        applies_to_rate_card_ids: string[] | null;
        is_active: boolean;
        priority: number;
        created_at: string;
        updated_at: string;
      }>;
    }>('/compensation/pay-rules'),

  createPayRule: (data: {
    name: string;
    description?: string | null;
    triggerType: string;
    triggerConfig: Record<string, unknown>;
    actionType: string;
    actionConfig: Record<string, unknown>;
    appliesToEmployeeIds?: string[] | null;
    appliesToRateCardIds?: string[] | null;
    isActive?: boolean;
    priority?: number;
  }) =>
    apiRequest<{ rule: { id: string; is_pending_approval?: boolean }; approval?: { id: string; status: string } }>(
      '/compensation/pay-rules', { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  updatePayRule: (id: string, data: Partial<{
    name: string; description: string | null;
    triggerType: string; triggerConfig: Record<string, unknown>;
    actionType: string; actionConfig: Record<string, unknown>;
    appliesToEmployeeIds: string[] | null; appliesToRateCardIds: string[] | null;
    isActive: boolean; priority: number;
  }>) =>
    apiRequest<{ rule: { id: string; is_pending_approval?: boolean }; approval?: { id: string; status: string } }>(
      `/compensation/pay-rules/${id}`, { method: 'PATCH', body: data as unknown as Record<string, unknown> }
    ),

  deletePayRule: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/compensation/pay-rules/${id}`, { method: 'DELETE' }),

  // S29 Item 5a — preview a candidate rule against historical entries.
  previewPayRule: (rule: Record<string, unknown>, days: number = 30) =>
    apiRequest<{
      summary: {
        totalEntries: number;
        entriesAffected: number;
        totalCurrentCents: number;
        totalCandidateCents: number;
        totalDeltaCents: number;
        fireCountByRule: Record<string, number>;
      };
      rows: Array<{
        entryId: string;
        workDate: string;
        baseCents: number;
        currentFinalCents: number;
        candidateFinalCents: number;
        deltaCents: number;
        wouldFire: boolean;
        firedRuleNames: string[];
        candidateMultiplier: number;
        candidateFlatCents: number;
      }>;
      truncated: boolean;
      daysScanned: number;
    }>('/compensation/pay-rules/preview', { method: 'POST', body: { rule, days } }),

  // S29 Item 5b — get conflicts among existing pay rules.
  getPayRuleConflicts: () =>
    apiRequest<{
      conflicts: Array<{
        kind: 'duplicate_trigger' | 'shadowing' | 'redundant_replace_card';
        ruleAId: string;
        ruleAName: string;
        ruleBId: string;
        ruleBName: string;
        message: string;
      }>;
      ruleCount: number;
    }>('/compensation/pay-rules/conflicts'),

  // ============================================================
  // Manager Portal (S27 Phase 4 — /manager/*)
  // requireManagerAccess() server-side resolves assignedEmployeeIds
  // and scopes every query. Managers see ONLY their assigned employees.
  // ============================================================
  listManagerEmployees: () =>
    apiRequest<{
      employees: Array<{
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        title: string | null;
        // W2-37: the real column is employees.hire_date (start_date never
        // existed — S35 lesson); the stale field name let the page read
        // undefined and render "—" for every row.
        hire_date: string | null;
        termination_date: string | null;
        employment_type: string | null;
        portal_status: string;
      }>;
    }>('/manager/employees'),

  getManagerEmployee: (id: string) =>
    apiRequest<{
      employee: {
        id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        title: string | null;
        start_date: string | null;
        termination_date: string | null;
        employment_type: string | null;
        portal_status: string;
        invited_at: string | null;
        activated_at: string | null;
      };
      recentEntries: Array<{
        id: string;
        work_date: string;
        hours_worked: string | null;
        daily_rate: string | null;
        gross_pay: string | null;
        status: string;
        attested_at: string | null;
      }>;
    }>(`/manager/employees/${id}`),

  listManagerApprovals: (status: 'pending' | 'approved' | 'rejected' = 'pending') =>
    apiRequest<{
      approvals: Array<{
        id: string;
        subject_type: string;
        subject_id: string;
        subject_summary: string | null;
        amount_cents: number | null;
        subject_date: string | null;
        required_role: string;
        status: string;
        created_at: string;
        reviewed_at: string | null;
        review_note: string | null;
        entry_id: string;
        work_date: string;
        hours_worked: string | null;
        daily_rate: string | null;
        gross_pay: string | null;
        facility_name: string | null;
        employee_id: string;
        first_name: string | null;
        last_name: string | null;
      }>;
    }>(`/manager/approvals?status=${encodeURIComponent(status)}`),

  decideManagerApproval: (id: string, data: { decision: 'approved' | 'rejected'; note?: string }) =>
    apiRequest<{ id: string; status: string; entryStatus: string }>(
      `/manager/approvals/${id}/decide`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  // Manager assignments — admin/owner CRUD on /hr/employees/:id (S27 Phase 4).
  listManagerAssignments: (employeeId: string) =>
    apiRequest<{
      assignments: Array<{
        id: string;
        manager_user_id: string;
        manager_email: string;
        manager_full_name: string | null;
        assigned_at: string;
      }>;
    }>(`/hr/employees/${employeeId}/manager-assignments`),

  assignManager: (employeeId: string, data: { managerUserId: string }) =>
    apiRequest<{ assignment: { id: string; manager_user_id?: string; alreadyExists?: boolean } }>(
      `/hr/employees/${employeeId}/manager-assignments`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  revokeManagerAssignment: (employeeId: string, data: { assignmentId: string; reason?: string }) =>
    apiRequest<{ revoked: boolean; id: string }>(
      `/hr/employees/${employeeId}/manager-assignments`,
      { method: 'DELETE', body: data as unknown as Record<string, unknown> }
    ),

  // Admin-side: list ALL documents (W-2 / paystub / etc) for one
  // employee. Used by /hr/employees/:id Documents section. Returns
  // metadata; the unified viewer mints presigned URLs on click.
  listHrEmployeeDocuments: (employeeId: string) =>
    apiRequest<{
      documents: Array<{
        id: string;
        docType: string;
        docYear: number | null;
        filename: string | null;
        sizeBytes: number | null;
        s3Key: string;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(`/hr/employees/${employeeId}/documents`),

  // Typeahead source for ManagerAssignmentsCard (S28 polish). Returns up to
  // 50 active entity members. Optional `q` for server-side ILIKE filter.
  listManagerCandidates: (q?: string) =>
    apiRequest<{
      candidates: Array<{
        id: string;
        email: string;
        full_name: string | null;
        role: string;
      }>;
    }>(`/hr/manager-candidates${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  // Pay periods (S27 Phase 5). Admin/owner only on backend.
  listPayPeriods: () =>
    apiRequest<{
      periods: Array<{
        id: string;
        period_start: string;
        period_end: string;
        cadence: string;
        status: string;
        closed_at: string | null;
        paid_at: string | null;
        approved_entry_count: number | null;
        pending_entry_count: number | null;
        locked_entry_count: number | null;
        notes: string | null;
        linked_entry_count: number;
      }>;
      entityCadence: string;
    }>('/pay-periods'),

  createPayPeriod: (data: { cadence: string; periodStart?: string; periodEnd?: string }) =>
    apiRequest<{ period: { id: string; period_start: string; period_end: string; status: string }; alreadyExists?: boolean }>(
      '/pay-periods', { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  getPayPeriodDetail: (id: string) =>
    apiRequest<{
      period: {
        id: string;
        period_start: string;
        period_end: string;
        cadence: string;
        status: string;
        closed_at: string | null;
        paid_at: string | null;
        approved_entry_count: number | null;
        pending_entry_count: number | null;
        locked_entry_count: number | null;
      };
      liveCounts: {
        approved: number;
        submitted: number;
        locked: number;
        paid: number;
        draft: number;
        rejected: number;
      };
    }>(`/pay-periods/${id}`),

  closePayPeriod: (id: string, data: { force?: boolean; cadence?: string } = {}) =>
    apiRequest<{
      id: string;
      status: string;
      approvedCount: number;
      pendingCount: number;
      lockedCount: number;
      nextPeriodId: string | null;
    }>(`/pay-periods/${id}/close`, { method: 'POST', body: data as unknown as Record<string, unknown> }),

  // Entity rename + metadata update. Owner/admin role only (server enforces).
  // S26 Phase 3: also handles time-entry subject labels + show-rates flag.
  updateEntity: (id: string, data: {
    legalName?: string; name?: string; specialty?: string; stateOfFormation?: string;
    ein?: string; dateOfFormation?: string; compensationModel?: string;
    // 2026-06-18 re-onboard ("Edit business setup") parity:
    onboardingPreset?: string; isSstb?: boolean;
    features?: {
      section105?: boolean; solo401k?: boolean; fmvValidation?: boolean;
      mileageTracking?: boolean; complianceCalendar?: boolean;
      payrollExport?: boolean; naturalLanguageQuery?: boolean;
    };
    // tax election is editable but a real change MUST carry the acknowledgment
    // (backend returns 400 AcknowledgmentRequired otherwise).
    taxElection?: string;
    taxElectionAck?: { accepted: boolean; acknowledgedText: string; priorElection?: string; nextElection?: string };
    timeSubjectLabel?: string; timeSubjectRequired?: boolean;
    timeSubjectSource?: 'free_text' | 'project_id' | 'client_id';
    showRatesToEmployees?: boolean;
    employeeW4SelfServe?: boolean; employeeAiEnabled?: boolean; weekendExcluded?: boolean;
  }) =>
    apiRequest<{ entity: { id: string; name: string; legal_name: string; tax_election: string; specialty: string | null; state_of_formation: string | null; ein: string | null; date_of_formation: string | null; compensation_model: string } }>(
      `/entities/${id}`, { method: 'PATCH', body: data as unknown as Record<string, unknown> }
    ),

  getW2Pct: () =>
    apiRequest<{ w2PctOfGross: number }>('/compensation/w2-pct'),

  updateW2Pct: (w2PctOfGross: number) =>
    apiRequest<{ w2PctOfGross: number }>('/compensation/w2-pct', { method: 'PATCH', body: { w2PctOfGross } }),

  // S-4 Reasonable Compensation Review — S-corp audit-defense tracker
  getCompensationReview: (year?: number) =>
    apiRequest<{
      year: number;
      entity: { id: string; legalName: string; taxElection: string; specialty: string | null; compensationModel: string; w2PctOfGross?: number };
      policy: { dailyRate: number; annualSalary: number; hourlyRate: number; version: string | null; effectiveDate: string | null; primaryRateCardLabel?: string | null; dailyRateSource?: 'compensation_policy' | 'rate_card' | 'none' };
      ytd: {
        daysWorked: number;
        rawGrossTotal?: number;
        impliedReasonableComp: number;
        actualW2Wages: number;
        payrollPeriodCount: number;
        variance: number;
        variancePct: number | null;
        annualizedImplied: number;
        annualizedActual: number;
        calendarProgressPct: number;
      };
      status: 'green' | 'amber' | 'red' | 'insufficient_data' | 'no_shifts' | 'no_payroll';
      statusNote: string;
      fmv: {
        specialty: string;
        region: string;
        survey_year: number;
        median_comp: number;
        percentile_25: number;
        percentile_75: number;
        source: string;
      } | null;
      fmvComparison: {
        annualizedValue: number;
        band: 'below_p25' | 'p25_to_median' | 'median_to_p75' | 'above_p75'
          | 'at_or_above_median' | 'below_median' | 'unknown';
        p25: number;
        median: number;
        p75: number;
        medianOnly?: boolean;
        defensibleIrs: boolean;
      } | null;
      policyDoc: { id: string; title: string | null; doc_type: string; signed_date: string | null; created_at: string } | null;
    }>(year ? `/compensation/review?year=${year}` : '/compensation/review'),

  generateReasonableCompMemo: () =>
    apiRequest<{
      downloadUrl: string;
      expiresInSeconds: number;
      fileName: string;
      bytes: number;
      sha256: string;
      year: number;
      generatedAt: string;
    }>('/compensation/review/memo', { method: 'POST' }),

  // S-7 Bank Reconciliation
  listBankStatements: (year?: number) =>
    apiRequest<{
      statements: Array<{
        id: string; bank_name: string | null; account_last4: string | null; account_nickname: string | null;
        account_type: string;
        period_start: string; period_end: string;
        opening_balance: string | null; closing_balance: string | null; computed_net_change: string | null;
        source_filename: string | null; source_format: string;
        reconciled_at: string | null; notes: string | null; created_at: string;
        tx_count: number; unmatched_count: number; fuzzy_count: number;
        matched_count: number; personal_count: number; internal_transfer_count: number;
      }>;
    }>(year ? `/finance/reconcile?year=${year}` : '/finance/reconcile'),

  uploadBankStatement: (data: {
    bankName?: string;
    accountLast4?: string;
    accountNickname?: string;
    periodStart: string;
    periodEnd: string;
    openingBalance?: number;
    closingBalance?: number;
    sourceFilename?: string;
    sourceFormat?: 'chase' | 'amex' | 'generic';
    accountType?: 'checking' | 'credit_card';
    rows: Array<Record<string, string | number | null>>;
  }) =>
    apiRequest<{
      statementId: string;
      transactionCount: number;
      autoMatched: number;
      fuzzyCount: number;
      unmatchedCount: number;
      netChange: number;
      closingBalance: number | null;
    }>('/finance/reconcile/upload', {
      method: 'POST',
      body: data as Record<string, unknown>,
    }),

  getBankStatementDetail: (statementId: string) =>
    apiRequest<{
      statement: Record<string, unknown>;
      transactions: Array<{
        id: string;
        transaction_date: string;
        amount: number;
        description: string | null;
        reference_number: string | null;
        running_balance: number | null;
        match_status: 'unmatched' | 'fuzzy' | 'matched' | 'personal' | 'split' | 'internal_transfer';
        matched_entity_type: string | null;
        matched_entity_id: string | null;
        matched_at: string | null;
        matched_by_name: string | null;
        personal_reason: string | null;
        notes: string | null;
        suggestions: Array<{
          type: string; id: string; counterparty: string;
          amount: number; date: string; score: number; reasons: string[];
        }>;
      }>;
    }>(`/finance/reconcile/${encodeURIComponent(statementId)}`),

  updateBankTransactionMatch: (data: {
    transactionId: string;
    matchStatus: 'matched' | 'personal' | 'unmatched' | 'internal_transfer';
    matchedEntityType?: string;
    matchedEntityId?: string;
    personalReason?: string;
    notes?: string;
  }) =>
    apiRequest<{ ok: true; transactionId: string; matchStatus: string }>(
      '/finance/reconcile/match',
      { method: 'POST', body: data as Record<string, unknown> }
    ),

  closeBankStatement: (statementId: string) =>
    apiRequest<{ ok: true; statementId: string; reconciledAt: string }>(
      '/finance/reconcile/close',
      { method: 'POST', body: { statementId } }
    ),

  deleteBankStatement: (statementId: string) =>
    apiRequest<{ ok: true }>(
      `/finance/reconcile/${encodeURIComponent(statementId)}`,
      { method: 'DELETE' }
    ),

  // Credit-card statement ingestion (Phase 2). Raw statement TEXT is parsed
  // + PAN-redacted server-side; the client never handles redaction. account_last4
  // / card_last4 are last-4 only (never a full PAN).
  listCcStatements: (year?: number) =>
    apiRequest<{
      statements: Array<{
        id: string; issuer: string | null; account_last4: string | null; account_nickname: string | null;
        period_start: string | null; period_end: string | null; statement_date: string | null;
        opening_balance: string | null; closing_balance: string | null;
        source_format: string; source_filename: string | null;
        status: 'parsed' | 'needs_review'; line_item_count: number;
        created_at: string; line_count: number; needs_review_count: number;
      }>;
    }>(year ? `/finance/cc-statements?year=${year}` : '/finance/cc-statements'),

  uploadCcStatement: (data: {
    fileContent: string;
    sourceFormat?: 'csv' | 'ofx' | 'qfx' | 'amex' | 'chase' | 'generic';
    sourceFilename?: string;
    issuer?: string;
    accountNickname?: string;
    periodStart?: string;
    periodEnd?: string;
    statementDate?: string;
    openingBalance?: number;
    closingBalance?: number;
  }) =>
    apiRequest<{
      statementId: string;
      status: 'parsed' | 'needs_review';
      lineItemCount: number;
    }>('/finance/cc-statements/upload', {
      method: 'POST',
      body: data as Record<string, unknown>,
    }),

  // Phase 7 — OCR a photographed / single-image card statement into rows.
  // Phase 7b extends this endpoint with two more modes (all back-compat):
  //   - scanned multi-page PDF: s3Keys[] (one presigned-PUT key per rasterized page)
  //   - free digital PDF: pre-parsed rows[] + engine:'pdf_text' (0 Textract pages)
  uploadCcStatementOcr: (data: {
    imageBase64?: string;
    s3Key?: string;
    s3Keys?: string[];                 // scanned multi-page (one key per page)
    ext?: string;
    contentType?: string;
    // Digital (text-layer PDF) — parsed client-side; supervisor re-redacts each row.
    rows?: Array<Record<string, unknown>>;
    source?: 'pdf_text';
    engine?: 'pdf_text';
    meta?: { accountLast4?: string | null };
    needsManualEntry?: boolean;
    reconciles?: boolean | null;
    droppedRowCount?: number;
    issuer?: string;
    accountNickname?: string;
    periodStart?: string;
    periodEnd?: string;
    statementDate?: string;
    statementTotal?: number;
  }) =>
    apiRequest<{
      statementId: string;
      status: 'parsed' | 'needs_review';
      lineItemCount: number;
      needsManualEntry: boolean;
      extractedRowCount?: number;
      droppedRowCount?: number;
      reconciles?: boolean | null;
    }>('/ingest/cc-statement-ocr', {
      method: 'POST',
      body: data as Record<string, unknown>,
    }),

  getCcStatement: (statementId: string) =>
    apiRequest<{
      statement: Record<string, unknown>;
      lineItems: Array<{
        id: string; line_number: number | null;
        txn_date: string | null; post_date: string | null;
        description: string | null; amount: string;
        reference: string | null; card_last4: string | null;
        // Phase 3 match fields
        match_status: 'unscored' | 'matched' | 'fuzzy' | 'unmatched' | 'orphan' | 'personal' | 'reversal';
        match_score: number | null;
        match_reasons: string[] | null;
        matched_entity_type: string | null;
        matched_entity_id: string | null;
        matched_at: string | null;
        possible_duplicate_expense_id: string | null;
        released_for_export: boolean;
        released_at: string | null;
        released_by: string | null;
        reverses_line_id: string | null;
        substantiation_basis: string | null;
        // Phase 8: live head reconstruction-memo status (null = no memo)
        memo_status: 'draft' | 'finalized' | 'superseded' | 'void' | null;
        created_at: string;
      }>;
      matchTiers: Record<string, number>;
      matchJob: {
        status: 'pending' | 'running' | 'complete' | 'failed';
        total_lines: number; scored_lines: number;
        started_at: string | null; finished_at: string | null; error: string | null;
      } | null;
    }>(`/finance/cc-statements/${encodeURIComponent(statementId)}`),

  deleteCcStatement: (statementId: string) =>
    apiRequest<{ ok: true }>(
      `/finance/cc-statements/${encodeURIComponent(statementId)}`,
      { method: 'DELETE' }
    ),

  // Phase 4 review actions on a card line.
  updateCcLineMatch: (lineId: string, data: {
    matchStatus: 'matched' | 'personal' | 'unmatched' | 'reversal';
    matchedEntityType?: string; matchedEntityId?: string; personalReason?: string;
    reversesLineId?: string; // required when matchStatus='reversal' (the charge it offsets)
  }) =>
    apiRequest<{ ok: true; lineId: string; matchStatus: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/match`,
      { method: 'POST', body: data as Record<string, unknown> }
    ),

  fileExpenseFromCcLine: (lineId: string, data: { category: string; businessPurpose?: string }) =>
    apiRequest<{ ok: true; lineId: string; expenseId: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/file-expense`,
      { method: 'POST', body: data as Record<string, unknown> }
    ),

  setCcLineReleased: (lineId: string, released: boolean) =>
    apiRequest<{ ok: true; lineId: string; released: boolean }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/release`,
      { method: 'POST', body: { released } }
    ),

  bulkCcLineAction: (action: 'release' | 'unrelease' | 'mark_personal', ids: string[]) =>
    apiRequest<{ ok: true; action: string; affected: number; requested: number }>(
      '/finance/cc-statements/lines/bulk',
      { method: 'POST', body: { action, ids } }
    ),

  // Phase 8 — §274(d) reconstruction memos on a matched card line.
  getCcMemo: (lineId: string) =>
    apiRequest<{ memo: CcMemo | null; revisions: Array<{ id: string; revised_at: string; revised_by: string | null; change_reason: string }> }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo`, { method: 'GET' }
    ),
  createCcMemo: (lineId: string, data: CcMemoInput) =>
    apiRequest<{ ok: true; memoId: string; lineId: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo`,
      { method: 'POST', body: data as Record<string, unknown> }
    ),
  updateCcMemo: (lineId: string, data: CcMemoInput & { changeReason?: string }) =>
    apiRequest<{ ok: true; memoId: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo`,
      { method: 'PATCH', body: data as Record<string, unknown> }
    ),
  // Finalize accepts the same fields as save-draft so a user who edits then finalizes (without a
  // separate save) freezes the CURRENT content, not stale DB content (post-build audit C1).
  finalizeCcMemo: (lineId: string, data?: CcMemoInput) =>
    apiRequest<{ ok: true; memoId: string; finalizedAt: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo/finalize`,
      { method: 'POST', body: (data || {}) as Record<string, unknown> }
    ),
  countersignCcMemo: (lineId: string) =>
    apiRequest<{ ok: true; memoId: string; countersignedAt: string }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo/countersign`, { method: 'POST' }
    ),
  amendCcMemo: (lineId: string, data: CcMemoInput) =>
    apiRequest<{ ok: true; memoId: string; amendmentNumber: number }>(
      `/finance/cc-statements/lines/${encodeURIComponent(lineId)}/memo/amend`,
      { method: 'POST', body: data as Record<string, unknown> }
    ),

  // Phase 9 — cardholder routing (Pro tier). Employee sees/classifies OWN card
  // charges; manager/admin reviews scoped lines + fixes a misroute.
  listMyCardholderLines: () =>
    apiRequest<{ lines: CcCardholderLine[] }>('/finance/cc-cardholder/mine', { method: 'GET' }),
  classifyMyCardholderLine: (lineId: string, classification: 'personal' | 'business', reason?: string) =>
    apiRequest<{ ok: true; classification: string }>(
      `/finance/cc-cardholder/lines/${encodeURIComponent(lineId)}/classify`,
      { method: 'POST', body: { classification, reason } }
    ),
  listCardholderReview: (status?: string) =>
    apiRequest<{ lines: CcCardholderLine[] }>(
      status ? `/finance/cc-cardholder/review?status=${encodeURIComponent(status)}` : '/finance/cc-cardholder/review',
      { method: 'GET' }
    ),
  reassignCardholderLine: (lineId: string, employeeId: string) =>
    apiRequest<{ ok: true; employeeId: string }>(
      `/finance/cc-cardholder/lines/${encodeURIComponent(lineId)}/reassign`,
      { method: 'POST', body: { employeeId } }
    ),

  // S-9 First-run checklist — Dashboard widget signal for new tenants.
  getFirstRunChecklist: () =>
    apiRequest<{
      show: boolean;
      items: Array<{
        id: string;
        label: string;
        description: string;
        done: boolean;
        linkTo: string;
        linkLabel: string;
        optional?: boolean;
      }>;
      completedCount: number;
      completedRequired: number;
      totalCount: number;
      requiredCount: number;
      entityCreatedAt: string | null;
      entityName: string | null;
      daysSinceCreation: number;
    }>('/me/first-run-checklist'),

  // Recent activity from audit_log (entity-scoped). Powers Dashboard recent
  // activity card and the standalone /activity page.
  getRecentActivity: (limit = 20) =>
    apiRequest<{ entries: Array<{ id: string; entity_id: string; user_id: string | null; user_name: string | null; agent_name: string; action: string; result: string; notes: string | null; created_at: string }> }>(`/audit-log?limit=${limit}`),

  // ---- HR (Phase 2a) ----
  // Employees CRUD (table from migration 025).
  listEmployees: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ employees: EmployeeRow[] }>(`/employees${qs}`);
  },
  getEmployee: (id: string) =>
    apiRequest<{ employee: EmployeeFull; portalLink: EmployeePortalLink | null }>(`/employees/${id}`),
  createEmployee: (data: Partial<EmployeeFull>) =>
    apiRequest<{ employee: EmployeeFull }>('/employees', { method: 'POST', body: data as Record<string, unknown> }),
  updateEmployee: (id: string, data: Partial<EmployeeFull>) =>
    apiRequest<{ employee: EmployeeFull }>(`/employees/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),

  // ---- Payroll state allocations (NS4 Week 3, migration 070) ----
  listPayPeriodStateAllocations: (payPeriodId: string) =>
    apiRequest<{
      period: { id: string; period_start: string; period_end: string; status: string };
      allocations: PayrollStateAllocation[];
    }>(`/pay-periods/${payPeriodId}/state-allocations`),
  upsertPayPeriodStateAllocation: (payPeriodId: string, data: {
    employeeId: string;
    state: string;
    grossWages: number;
    daysWorked?: number;
    hoursWorked?: number;
    stateWithholding?: number;
    source?: 'manual' | 'time_entries_auto' | 'imported';
    notes?: string;
  }) =>
    apiRequest<{ allocation: PayrollStateAllocation }>(
      `/pay-periods/${payPeriodId}/state-allocations`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),
  deletePayPeriodStateAllocation: (payPeriodId: string, allocationId: string) =>
    apiRequest<{ deleted: { id: string; employee_id: string; state: string } }>(
      `/pay-periods/${payPeriodId}/state-allocations/${allocationId}`,
      { method: 'DELETE' }
    ),

  // ---- Employee onboarding checklist (NS4 followup R6, migration 072) ----
  listEmployeeOnboarding: (employeeId: string) =>
    apiRequest<{
      employee: { id: string; first_name: string | null; last_name: string | null; email: string | null; hire_date: string | null; status: string };
      items: EmployeeOnboardingItem[];
      categories: Record<string, string>;
    }>(`/employees/${employeeId}/onboarding`),
  seedEmployeeOnboarding: (employeeId: string) =>
    apiRequest<{ inserted: number; skipped: number; total: number }>(
      `/employees/${employeeId}/onboarding/seed`,
      { method: 'POST', body: {} }
    ),
  addEmployeeOnboardingCustomItem: (employeeId: string, data: {
    title: string;
    description?: string;
    externalLink?: string;
    category?: string;
  }) =>
    apiRequest<{ item: EmployeeOnboardingItem }>(
      `/employees/${employeeId}/onboarding/custom-items`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),
  updateEmployeeOnboardingItem: (itemId: string, data: {
    status?: 'pending' | 'in_progress' | 'complete' | 'blocked' | 'not_applicable';
    notes?: string;
    blockedReason?: string;
  }) =>
    apiRequest<{ item: EmployeeOnboardingItem }>(
      `/onboarding-items/${itemId}`,
      { method: 'PATCH', body: data as unknown as Record<string, unknown> }
    ),
  deleteEmployeeOnboardingItem: (itemId: string) =>
    apiRequest<{ deleted: boolean; id: string }>(
      `/onboarding-items/${itemId}`,
      { method: 'DELETE' }
    ),

  // ---- Retention attestations (NS4 Week 3, migration 069) ----
  listRetentionAttestations: () =>
    apiRequest<{ attestations: RetentionAttestation[] }>('/retention-attestations'),
  createRetentionAttestation: (data: {
    taxYear: number;
    notes?: string;
    scope?: Record<string, unknown>;
  }) =>
    apiRequest<{ attestation: RetentionAttestation }>('/retention-attestations', {
      method: 'POST', body: data as unknown as Record<string, unknown>,
    }),

  // ---- Employee Portal admin ops (S25 Phase 1, S26 Phase 2) ----
  inviteEmployee: (employeeId: string, data: { email: string; expiresInHours?: number }) =>
    apiRequest<{ linkId: string; employeeId: string; email: string; expiresAt: string }>(
      `/hr/employees/${employeeId}/invite`, { method: 'POST', body: data as Record<string, unknown> }
    ),
  resendInvitation: (employeeId: string, data?: { expiresInHours?: number }) =>
    apiRequest<{ linkId: string; email: string; expiresAt: string }>(
      `/hr/employees/${employeeId}/invite/resend`, { method: 'POST', body: (data || {}) as Record<string, unknown> }
    ),
  revokeInvitation: (employeeId: string, reason?: string) =>
    apiRequest<{ revokedLinkId: string }>(
      `/hr/employees/${employeeId}/invite`, { method: 'DELETE', body: { reason } as Record<string, unknown> }
    ),
  terminateEmployee: (employeeId: string, data: { reason: string; effectiveDate: string; lastPaycheckDate?: string | null }) =>
    apiRequest<{
      employeeId: string;
      terminatedAt: string;
      effectiveDate: string;
      cognitoSignedOut: boolean;
      portalAccessRevoked: boolean;
    }>(`/hr/employees/${employeeId}/terminate`, { method: 'POST', body: data as Record<string, unknown> }),

  // Activation (PUBLIC; no auth header — invitee doesn't have a Cognito
  // account yet). Path is /v1/employee/activate/* per CDK explicit routes.
  validateActivationToken: (token: string) =>
    apiRequest<{
      valid: boolean;
      employeeName?: string;
      entityName?: string;
      expiresAt?: string;
      requiresPasswordSetup?: boolean;
      w4SelfServe?: boolean;
      error?: string;
    }>(`/employee/activate/validate/${encodeURIComponent(token)}`, { skipAuth: true, skipEntityHeader: true }),
  activateEmployeeAccount: (token: string, data: { password: string; fullName: string }) =>
    apiRequest<{
      idToken: string;
      accessToken: string;
      refreshToken: string;
      redirectTo: string;
      entityId: string;
    }>(`/employee/activate/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: data as Record<string, unknown>,
      skipAuth: true,
      skipEntityHeader: true,
    }),

  // PTO ledger + workflow (migration 026).
  getEmployeePTO: (employeeId: string, params?: { limit?: number }) => {
    const qs = params?.limit ? `?limit=${params.limit}` : '';
    return apiRequest<{
      employee: { id: string; firstName: string; lastName: string; hireDate: string | null; state: string | null };
      balances: PTOBalance[];
      stateRule: PTOStateRule;
      ledger: PTOAccrualEntry[];
      requests: PTORequest[];
    }>(`/employees/${employeeId}/pto${qs}`);
  },
  accruePTO: (employeeId: string, data: { leaveType: string; hours: number; effectiveDate: string; source?: string; notes?: string }) =>
    apiRequest<{ accrual: PTOAccrualEntry }>(`/employees/${employeeId}/pto/accrue`, { method: 'POST', body: data as Record<string, unknown> }),
  submitPTORequest: (employeeId: string, data: { leaveType?: string; startDate: string; endDate: string; hoursRequested: number; reason?: string }) =>
    apiRequest<{ request: PTORequest }>(`/employees/${employeeId}/pto/requests`, { method: 'POST', body: data as Record<string, unknown> }),
  listPTORequests: (params?: { status?: string; employeeId?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ requests: Array<PTORequest & { first_name: string; last_name: string }> }>(`/pto/requests${qs}`);
  },
  decidePTORequest: (requestId: string, data: { decision: 'approved' | 'denied'; notes?: string }) =>
    apiRequest<{ requestId: string; decision: string }>(`/pto/requests/${requestId}/decide`, { method: 'POST', body: data as Record<string, unknown> }),
  cancelPTORequest: (requestId: string, notes?: string) =>
    apiRequest<{ requestId: string; status: string }>(`/pto/requests/${requestId}/cancel`, { method: 'POST', body: { notes } }),

  // ---- Scheduling Phase B-E — manager approval queue for time off ----
  listManagerPTORequests: (status: 'submitted' | 'approved' | 'denied' | 'canceled' = 'submitted') =>
    apiRequest<{ requests: ManagerPTORequest[] }>(`/manager/pto-requests?status=${encodeURIComponent(status)}`),
  // decideManagerPTORequest delegates server-side to the pre-existing
  // decidePTORequest() handler (round-1 audit caught the response type had
  // drifted from that reality) — the real success shape is
  // {requestId, decision}, not {id, status}.
  decideManagerPTORequest: (id: string, data: { decision: 'approved' | 'denied'; note?: string }) =>
    apiRequest<{ requestId: string; decision: string }>(`/manager/pto-requests/${id}/decide`, { method: 'POST', body: data as unknown as Record<string, unknown> }),

  // ---- Scheduling Phase B-E — "who's free" capacity + combined calendar ----
  getScheduleCapacity: (start: string, end: string) =>
    apiRequest<{ start: string; end: string; employees: CapacityEmployee[] }>(
      `/schedule/capacity?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
    ),
  getCombinedSchedule: (start: string, end: string) =>
    apiRequest<{ start: string; end: string; entities: CombinedScheduleEntity[] }>(
      `/schedule/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
    ),
  // Round-1 audit fix: a company's own combine-flag only affects how it
  // appears on OTHER companies' combined views (getCombinedSchedule always
  // shows the active entity unconditionally) — so this always needs an
  // explicit targetEntityId, which may differ from whichever entity is
  // currently active.
  setCombinedCalendarPreference: (targetEntityId: string, enabled: boolean) =>
    apiRequest<{ entityId: string; enabled: boolean }>('/me/combined-calendar-preference', { method: 'PATCH', body: { targetEntityId, enabled } }),
  listCombinedCalendarPreferences: () =>
    apiRequest<{ preferences: Array<{ entity_id: string; enabled: boolean; entity_name: string; canEdit: boolean }> }>('/me/combined-calendar-preferences'),

  // ---- Scheduling Phase B-E — Calendar Sync (Google + Apple) ----
  // Round-1 audit P1 fix: state is a server-minted, single-use CSRF token
  // (see migration 138's oauth_pending_states) — Google echoes it back
  // verbatim on the redirect, and connectGoogleCalendar verifies it belongs
  // to the caller before ever exchanging the code. The frontend never
  // generates or stores it itself, just carries it through.
  getGoogleCalendarAuthUrl: (redirectUri: string) =>
    apiRequest<{ url: string }>('/calendar-connections/google/auth-url', { method: 'POST', body: { redirectUri } }),
  connectGoogleCalendar: (code: string, redirectUri: string, state: string) =>
    apiRequest<{ connection: CalendarConnection }>('/calendar-connections/google/connect', { method: 'POST', body: { code, redirectUri, state } }),
  connectAppleCalendar: (email: string, appSpecificPassword: string) =>
    apiRequest<{ connection: CalendarConnection | null; warning?: string }>('/calendar-connections/apple/connect', {
      method: 'POST', body: { email, appSpecificPassword },
    }),
  listCalendarConnections: () =>
    apiRequest<{ connections: CalendarConnection[] }>('/calendar-connections'),
  listAvailableCalendars: (id: string) =>
    apiRequest<{ calendars: Array<CalendarConnectionCalendar & { selected: boolean }> }>(`/calendar-connections/${id}/calendars`),
  updateCalendarConnectionSelection: (id: string, selectedCalendars: CalendarConnectionCalendar[]) =>
    apiRequest<{ connection: CalendarConnection }>(`/calendar-connections/${id}`, { method: 'PATCH', body: { selectedCalendars } as unknown as Record<string, unknown> }),
  syncCalendarConnection: (id: string) =>
    apiRequest<{ synced: number; status: string; warning?: string; message?: string }>(`/calendar-connections/${id}/sync`, { method: 'POST' }),
  disconnectCalendarConnection: (id: string) =>
    apiRequest<{ ok: boolean }>(`/calendar-connections/${id}`, { method: 'DELETE' }),

  // Holidays
  listHolidays: (year?: number) => {
    const qs = year ? `?year=${year}` : '';
    return apiRequest<{ holidays: HolidayEntry[] }>(`/holidays${qs}`);
  },
  createHoliday: (data: { observedDate: string; name: string; jurisdiction?: string; isPaid?: boolean; isFloating?: boolean; notes?: string }) =>
    apiRequest<{ holiday: HolidayEntry }>('/holidays', { method: 'POST', body: data as Record<string, unknown> }),
  seedFederalHolidays: (year: number) =>
    apiRequest<{ year: number; attempted: number; inserted: number }>('/holidays/seed-federal', { method: 'POST', body: { year } }),

  listStatePTORules: () =>
    apiRequest<{ rules: PTOStateRule[] }>('/pto/state-rules'),

  // ---- Finance: Bills (Phase 2b) ----
  listBills: (params?: { status?: string; vendor?: string; year?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ bills: Bill[] }>(`/bills${qs}`);
  },
  getBill: (id: string) =>
    apiRequest<{ bill: Bill; lineItems: BillLineItem[]; payments: BillPayment[] }>(`/bills/${id}`),
  createBill: (data: Partial<Bill> & { vendorName: string; billDate: string; dueDate: string; total: number; lineItems?: Partial<BillLineItem>[] }) =>
    apiRequest<{ bill: Bill }>('/bills', { method: 'POST', body: data as Record<string, unknown> }),
  updateBill: (id: string, data: Partial<Bill>) =>
    apiRequest<{ bill: Bill }>(`/bills/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  recordBillPayment: (
    billId: string,
    data: {
      amount: number;
      paymentDate: string;
      paymentMethod?: string;
      referenceNumber?: string;
      notes?: string;
      /**
       * P1.4 audit Finding A-11. When the bill's payee_contractor lacks a W-9,
       * the operator must record an IRC §3406 backup-withholding decision:
       *   'apply'   — record 24% of bill.total as backup withholding (Box 4)
       *   'decline' — explicitly decline withholding (acknowledged risk)
       * Missing field → backend records the payment but flags the bill as
       * "decision missing" + returns a warning in the response body.
       */
      backupWithholdingDecision?: 'apply' | 'decline';
    },
  ) =>
    apiRequest<{
      payment: BillPayment;
      /** Present when contractor lacks W-9 or operator action was taken. */
      backupWithholding?: {
        code: 'BACKUP_WITHHOLDING_APPLIED' | 'BACKUP_WITHHOLDING_DECLINED' | 'BACKUP_WITHHOLDING_DECISION_MISSING';
        message: string;
        contractorName?: string;
        withheldCents?: number;
        suggestedWithholdingCents?: number;
      };
    }>(`/bills/${billId}/payments`, { method: 'POST', body: data as Record<string, unknown> }),

  // ---- Owner distributions (A2 owner-draw ledger) ----
  listOwnerDistributions: (params?: { year?: string; userId?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ distributions: OwnerDistribution[]; totalCents: number }>(`/owner-distributions${qs}`);
  },
  createOwnerDistribution: (data: { distributionDate: string; amountCents: number; method?: OwnerDistribution['method']; note?: string }) =>
    apiRequest<{ distribution: OwnerDistribution }>('/owner-distributions', { method: 'POST', body: data as Record<string, unknown> }),
  deleteOwnerDistribution: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/owner-distributions/${id}`, { method: 'DELETE' }),

  // ---- Schedule: expected revenue (Home Redesign — paid-for-work forecast) ----
  getExpectedRevenue: (params?: { today?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<ExpectedRevenueSummary>(`/schedule/expected-revenue${qs}`);
  },

  // ---- Personal "You" lens (Home Redesign — owner cross-entity rollup) ----
  getPersonalOverview: (params?: { year?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<PersonalOverview>(`/me/overview${qs}`);
  },
  // Persist the Household toggle (writes the caller's own user_tax_profiles.household_view).
  setHouseholdView: (household: boolean, year?: number) =>
    apiRequest<{ household: boolean; year: number }>('/me/household', {
      method: 'POST',
      body: { household, ...(year ? { year } : {}) },
    }),

  getAPAging: () =>
    apiRequest<{ buckets: APAgingBuckets; topVendors: Array<{ vendor_name: string; bill_count: number; outstanding: string }> }>('/bills/aging'),

  // NS4 R3.3 — per-entity nav-badge counts.
  // NS4 R7 — policiesReviewNeeded + onboardingUnstarted added.
  // Sprint 1.5 (2026-05-28) — aiBudgetPct added for Layer 2 Smart Attention row.
  getNavCounts: () =>
    apiRequest<{
      overdueBills: number;
      pendingApprovals: number;
      pendingPto: number;
      missingW9: number;
      complianceDueSoon: number;
      policiesReviewNeeded?: number;
      onboardingUnstarted?: number;
      aiBudgetPct?: number;
      textractPagesUsed?: number;
      textractPagesIncluded?: number;
    }>('/nav/counts'),

  // Sprint 1 — Command Center home feed.
  // Returns a ranked priority queue scoped to the user's role + entity preset.
  // Server caches 60s per (entity, user, cadence); invalidate by dispatching
  // `nexus:nav-counts-refresh` on the window after any source-table write.
  getCommandCenterFeed: (cadence: 'today' | 'week', limit?: number) =>
    apiRequest<{
      cadence: 'today' | 'week';
      entity_id: string;
      preset: string;
      user_role: string;
      generated_at: string;
      items: Array<{
        id: string;
        card_type:
          | 'compliance'
          | 'collection'
          | 'payment'
          | 'renewal'
          | 'approval'
          | 'pto'
          | 'categorization'
          | 'checklist'
          | 'anomaly'
          | 'onboarding';
        title: string;
        subtitle: string | null;
        amount_cents: number | null;
        due_date: string | null;
        days_until: number;
        owner_role: string | null;
        estimated_minutes: number | null;
        priority_score: number;
        urgency_tier?: 'overdue' | 'today' | 'this_week' | 'this_month' | 'future';
        click_route: string;
        source_table: string;
        source_id: string;
      }>;
      total_items_considered: number;
      filtered_by_role: number;
      ranking_method: string;
      read_only?: boolean;
      cached?: boolean;
    }>(
      `/command-center/feed?cadence=${cadence}${limit ? `&limit=${limit}` : ''}`
    ),

  getCashFlowForecast: (weeks = 13, scenario: string = 'baseline') =>
    apiRequest<{
      weeks: number;
      generatedAt: string;
      scenarioKey: string;
      scenarioLabel?: string;
      scenarioDescription?: string;
      availableScenarios: Record<string, { label: string; description: string }>;
      forecast: CashFlowWeek[];
      summary: { totalInflow: number; totalOutflow: number; netChange: number };
      baseline?: {
        forecast: CashFlowWeek[];
        summary: { totalInflow: number; totalOutflow: number; netChange: number };
      };
      delta?: { inflow: number; outflow: number; netChange: number };
      scenarioMeta?: { lostClientId?: string; lostAmount?: number } | null;
      caveats: string[];
    }>(`/finance/cashflow?weeks=${weeks}&scenario=${encodeURIComponent(scenario)}`),

  // ---- Projects (Phase 2c) ----
  listProjects: (params?: { status?: string; clientId?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ projects: Array<Project & { client_name: string | null }> }>(`/projects${qs}`);
  },
  getProject: (id: string) =>
    apiRequest<{
      project: Project & { client_name: string | null };
      profitability: ProjectProfitability | null;
      expenses: Array<{ id: string; vendor: string; amount: string; expense_date: string; category: string }>;
      timeEntries: Array<{ id: string; work_date: string; hours_worked: string; daily_rate: string | null; project_code: string | null; description: string | null }>;
      invoices: Array<{ id: string; invoice_number: string; invoice_date: string; total: string; amount_paid: string; status: string }>;
      bills: Array<{ id: string; vendor_name: string; bill_number: string | null; total: string; amount_paid: string; status: string; due_date: string }>;
    }>(`/projects/${id}`),
  createProject: (data: Partial<Project> & { name: string }) =>
    apiRequest<{ project: Project }>('/projects', { method: 'POST', body: data as Record<string, unknown> }),
  updateProject: (id: string, data: Partial<Project>) =>
    apiRequest<{ project: Project }>(`/projects/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  getProjectProfitability: (id: string) =>
    apiRequest<ProjectProfitability>(`/projects/${id}/profitability`),
  listProjectProfitability: (params?: { status?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ projects: Array<ProjectProfitability & { client_id: string | null; client_name: string | null }> }>(`/projects/profitability${qs}`);
  },

  // ---- Owner Continuity (Phase 2d) ----
  // Single aggregate fetch for the /continuity page: plan + docs +
  // beneficiaries + contacts + buy-sell + status rollup.
  getContinuityBundle: () =>
    apiRequest<ContinuityBundle>('/continuity'),
  upsertContinuityPlan: (data: Partial<Pick<ContinuityPlan, 'status' | 'ownerInstructions' | 'reviewIntervalMonths'>>) =>
    apiRequest<{ plan: ContinuityPlan }>('/continuity/plan', { method: 'PUT', body: data as Record<string, unknown> }),
  markContinuityPlanReviewed: () =>
    apiRequest<{ plan: ContinuityPlan }>('/continuity/plan/mark-reviewed', { method: 'POST' }),
  markContinuityDocumentReviewed: (id: string) =>
    apiRequest<{ row: ContinuityDocument }>(`/continuity/documents/${id}/mark-reviewed`, { method: 'POST' }),
  createContinuityDocument: (data: Partial<ContinuityDocument> & { docType: string; title: string }) =>
    apiRequest<{ document: ContinuityDocument }>('/continuity/documents', { method: 'POST', body: data as Record<string, unknown> }),
  updateContinuityDocument: (id: string, data: Partial<ContinuityDocument>) =>
    apiRequest<{ document: ContinuityDocument }>(`/continuity/documents/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deleteContinuityDocument: (id: string) =>
    apiRequest<{ deleted: boolean }>(`/continuity/documents/${id}`, { method: 'DELETE' }),
  createBeneficiary: (data: Partial<BeneficiaryDesignation> & { assetType: string; assetDescription: string; beneficiaryName: string }) =>
    apiRequest<{ beneficiary: BeneficiaryDesignation }>('/continuity/beneficiaries', { method: 'POST', body: data as Record<string, unknown> }),
  updateBeneficiary: (id: string, data: Partial<BeneficiaryDesignation>) =>
    apiRequest<{ beneficiary: BeneficiaryDesignation }>(`/continuity/beneficiaries/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deleteBeneficiary: (id: string) =>
    apiRequest<{ deleted: boolean }>(`/continuity/beneficiaries/${id}`, { method: 'DELETE' }),
  createEmergencyContact: (data: Partial<EmergencyContact> & { role: string; name: string }) =>
    apiRequest<{ contact: EmergencyContact }>('/continuity/contacts', { method: 'POST', body: data as Record<string, unknown> }),
  updateEmergencyContact: (id: string, data: Partial<EmergencyContact>) =>
    apiRequest<{ contact: EmergencyContact }>(`/continuity/contacts/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deleteEmergencyContact: (id: string) =>
    apiRequest<{ deleted: boolean }>(`/continuity/contacts/${id}`, { method: 'DELETE' }),
  upsertBuySell: (data: Partial<BuySellTerms>) =>
    apiRequest<{ buySell: BuySellTerms }>('/continuity/buy-sell', { method: 'PUT', body: data as Record<string, unknown> }),

  // Payroll
  getPayrollStage: () =>
    apiRequest('/payroll/stage'),

  exportPayroll: () =>
    apiRequest('/payroll/export', { method: 'POST' }),

  generateW2: (params: { year: number; employeeName?: string; employeeSsnLast4?: string; employeeAddress?: string; employeeCity?: string; employeeState?: string; employeeZip?: string; federalWithheld?: number; stateCode?: string; stateWages?: number; stateWithheld?: number; localWages?: number; localWithheld?: number; localityName?: string }) => {
    const qs = new URLSearchParams(Object.entries(params).reduce((acc, [k, v]) => { if (v !== undefined && v !== '') acc[k] = String(v); return acc; }, {} as Record<string, string>)).toString();
    return apiRequest<{ fileName: string; fileKey: string; downloadUrl: string; year: number; employeeName: string; employerName: string; summary: Record<string, number> }>(`/payroll/w2?${qs}`);
  },

  // Reports
  getPL: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/reports/pl${qs}`);
  },

  // Unified document viewer endpoint (S28). Returns presigned URL + OCR/AI
  // digest for any of 8 source types: receipt, contract, policy,
  // vendor_agreement, contractor_agreement, continuity_document,
  // employee_document, credential. Owner-tier (viewer+ role).
  getDocumentView: (source: string, id: string) =>
    apiRequest<{
      source: string;
      id: string;
      title: string;
      filename: string | null;
      mimeType: string;
      sizeBytes: number | null;
      s3Key: string;
      presignedUrl: string;
      expiresAt: string;
      /** Non-null when malware scanning couldn't verify this file (not a block — see the audit doc). */
      scanWarning: string | null;
      digest: {
        kind: 'textract_receipt' | 'ai_contract' | 'fields_only';
        fields: Array<{ label: string; value: string; mono?: boolean; confidence?: number }>;
        lineItems?: Array<{ description?: string; amount?: number | string; quantity?: number | string }>;
        aiSummary?: string | null;
        aiSummaryGeneratedAt?: string | null;
        aiFields?: Array<{ label: string; value: string; mono?: boolean }>;
        raw?: unknown;
      };
      createdAt: string;
      updatedAt: string;
    }>(`/documents/view?source=${encodeURIComponent(source)}&id=${encodeURIComponent(id)}`),

  // Doc Vault P2: lazy AI digest for a text-bearing document (contract, policy,
  // continuity_document, contractor_agreement, vendor_agreement). Haiku, cached
  // by content hash + budget-gated server-side; returns the summary or a skip
  // status (no text / over budget).
  generateDocumentDigest: (source: string, id: string) =>
    apiRequest<{ summary: string | null; status: string; injectionFlagged?: boolean }>('/documents/digest', {
      method: 'POST',
      body: { source, id },
    }),

  // Consolidated cross-entity cash flow (Phase Three chunk 2, S28).
  // Per-entity 13-week (configurable) headline forecast: starting cash
  // from reconciled bank statement + expected inflow + expected outflow
  // + net + ending balance. Totals row sums everything across entities.
  getOrgConsolidatedCashFlow: (params?: { weeks?: number }) => {
    const qs = params?.weeks
      ? `?weeks=${params.weeks}`
      : '';
    return apiRequest<{
      weeks: number;
      entityCount: number;
      entities: Array<{
        id: string;
        legalName: string;
        taxElection: string | null;
        role: string;
        startingBalance: number;
        expectedInflow: number;
        expectedOutflow: number;
        expectedNet: number;
        endingBalance: number;
        openInvoiceCount: number;
        openBillCount: number;
        anchorDate: string | null;
        anchorAccount: string | null;
        error?: string;
      }>;
      totals: {
        startingBalance: number;
        expectedInflow: number;
        expectedOutflow: number;
        expectedNet: number;
        endingBalance: number;
        openInvoiceCount: number;
        openBillCount: number;
      };
      generatedAt: string;
    }>(`/org/cashflow${qs}`);
  },

  // Consolidated cross-entity AR/AP aging (Phase Three chunk 3, S28).
  // Per-entity invoices outstanding (AR) + bills outstanding (AP) bucketed
  // by days past due (current / 30 / 60 / 90 / 90+). Totals row sums
  // each bucket across entities. netWorkingCapital = ar - ap per entity.
  getOrgConsolidatedARAP: () =>
    apiRequest<{
      entityCount: number;
      entities: Array<{
        id: string;
        legalName: string;
        taxElection: string | null;
        role: string;
        arCurrent: number;
        ar30: number;
        ar60: number;
        ar90: number;
        arOver90: number;
        arTotal: number;
        arOpenCount: number;
        apCurrent: number;
        ap30: number;
        ap60: number;
        ap90: number;
        apOver90: number;
        apTotal: number;
        apOpenCount: number;
        netWorkingCapital: number;
        error?: string;
      }>;
      totals: {
        arCurrent: number; ar30: number; ar60: number; ar90: number; arOver90: number; arTotal: number;
        apCurrent: number; ap30: number; ap60: number; ap90: number; apOver90: number; apTotal: number;
        netWorkingCapital: number;
      };
      generatedAt: string;
    }>('/org/ar-ap'),

  // Consolidated cross-entity P&L (Phase Three first chunk, S28).
  // Owner/admin only on the backend. Returns one row per accessible entity
  // plus a totals row. month optional — omit for full-year.
  getOrgConsolidatedPL: (params?: { year?: number; month?: number }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params).reduce((acc, [k, v]) => {
            if (v !== undefined) acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>)
        ).toString()
      : '';
    return apiRequest<{
      period: { year: number; month: number | null };
      entityCount: number;
      entities: Array<{
        id: string;
        legalName: string;
        taxElection: string | null;
        specialty?: string | null;
        compensationModel?: string | null;
        role: string;
        revenue: number;
        expenses: number;
        deductibleExpenses: number;
        wages: number;
        employerTaxes: number;
        grossProfit: number;
        netIncome: number;
        error?: string;
      }>;
      totals: {
        revenue: number; expenses: number; deductibleExpenses: number;
        wages: number; employerTaxes: number; grossProfit: number; netIncome: number;
      };
      generatedAt: string;
    }>(`/org/pl${qs}`);
  },

  // Phase Three #1 (post-S30, migration 055): cross-entity intercompany volume.
  // Owner/admin only. Returns rows for every (source_entity, target_entity, kind)
  // pair within the caller's org for the requested year (default current).
  // Drives the "Intercompany volume" card on /consolidated.
  getOrgIntercompanyVolume: (params?: { year?: number }) => {
    const qs = params?.year ? `?year=${params.year}` : '';
    return apiRequest<{
      year: number;
      entityCount: number;
      pairCount: number;
      pairs: Array<{
        sourceEntityId: string;
        sourceLegalName: string;
        targetEntityId: string;
        targetLegalName: string;
        taxYear: number;
        kind: 'invoice' | 'bill' | 'expense' | 'income';
        amount: number;
        rowCount: number;
        amountPaid: number | null;
        amountOutstanding: number | null;
      }>;
      totals: {
        invoices: number; bills: number; expenses: number; income: number;
        allKinds: number;
      };
      generatedAt: string;
    }>(`/org/intercompany${qs}`);
  },

  getExpenseSummary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/reports/expense-summary${qs}`);
  },

  // Tax
  getEstimatedPayments: () =>
    apiRequest('/tax/estimated-payments'),

  get401kHeadroom: () =>
    apiRequest('/tax/401k/headroom'),

  list401kContributions: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/tax/401k/contributions${qs}`);
  },

  add401kContribution: (data: Record<string, unknown>) =>
    apiRequest('/tax/401k/contributions', { method: 'POST', body: data }),

  recordTaxPayment: (data: Record<string, unknown>) =>
    apiRequest('/tax/payments', { method: 'POST', body: data }),

  getTaxProjection: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/tax/projection${qs}`);
  },

  getPersonalTaxProfile: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/tax/personal-profile${qs}`);
  },

  savePersonalTaxProfile: (data: Record<string, unknown>) =>
    apiRequest('/tax/personal-profile', { method: 'POST', body: data }),

  /**
   * Multi-state income apportionment for a given year. Breaks attested
   * days by work_state and applies each state's 2026 rate (flat or
   * bracketed) to the days-weighted share of total income.
   */
  getStateApportionment: (params?: { year?: string; filingStatus?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{
      year: number;
      method: string;
      totalDays: number;
      totalIncome: number;
      byState: Array<{
        state: string;
        daysWorked: number;
        apportionPct: number;
        incomeAllocatedByDays: number;
        incomeAttestedDirect: number;
        estimatedStateTax: number;
        rateNote: string | null;
      }>;
      unattributedDays?: number;
      warnings?: string[];
      note?: string;
    }>(`/tax/apportionment${qs}`);
  },

  // S30B sprint: Tax Optimizer (admin-only what-if simulator).
  // POST /tax/optimize with { year, knobs } -> { base, scenario, delta, ... }
  // S31 added citations + recommendations to the response.
  runTaxOptimizer: (knobs: Record<string, number | string>, year?: number) =>
    apiRequest<TaxOptimizerResponse>('/tax/optimize', { method: 'POST', body: { knobs, year } }),

  // PP2 — S-corp break-even. Empty body returns entity-derived defaults +
  // comparison; pass overrides to recompute. Owner/admin-gated.
  getBreakEven: (overrides?: Record<string, number | string | boolean | null>) =>
    apiRequest<BreakEvenResponse>('/tax/break-even', { method: 'POST', body: overrides || {} }),

  // Bundle 5.1b-b Phase 3: entities.filer_type override for state-side
  // depreciation. NULL filerType clears the override (restore auto-derive).
  // Otherwise must be one of FT enum values.
  getEntityFilerType: () =>
    apiRequest<EntityFilerTypeResponse>('/tax/entity/filer-type'),
  updateEntityFilerType: (filerType: 'default' | 'corp' | 'pit' | 'pass_through' | null) =>
    apiRequest<{ entityId: string; filerType: string | null; prior: string | null; ok: boolean }>(
      '/tax/entity/filer-type',
      { method: 'PATCH', body: { filerType } }
    ),

  // S31: persisted Tax Optimizer scenarios.
  listTaxScenarios: (params?: { year?: string; includeArchived?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ scenarios: TaxScenario[] }>(`/tax/scenarios${qs}`);
  },
  createTaxScenario: (body: {
    name: string;
    description?: string | null;
    knobs: Record<string, number | string>;
    taxYear?: number;
    status?: 'draft' | 'saved';
    isStarred?: boolean;
  }) =>
    apiRequest<{ scenario: TaxScenario }>('/tax/scenarios', { method: 'POST', body }),
  getTaxScenario: (id: string) =>
    apiRequest<{ scenario: TaxScenario }>(`/tax/scenarios/${id}`),
  updateTaxScenario: (id: string, body: Partial<{
    name: string;
    description: string | null;
    knobs: Record<string, number | string>;
    isStarred: boolean;
    status: 'draft' | 'saved' | 'applied' | 'archived';
  }>) =>
    apiRequest<{ scenario: TaxScenario }>(`/tax/scenarios/${id}`, { method: 'PATCH', body }),
  deleteTaxScenario: (id: string) =>
    apiRequest<{ success: true; id: string }>(`/tax/scenarios/${id}`, { method: 'DELETE' }),
  markTaxScenarioApplied: (id: string, body?: { note?: string | null }) =>
    apiRequest<{ scenario: TaxScenario }>(`/tax/scenarios/${id}/apply`, { method: 'POST', body: body || {} }),
  rerunTaxScenario: (id: string) =>
    apiRequest<{ scenario: TaxScenario }>(`/tax/scenarios/${id}/rerun`, { method: 'POST', body: {} }),
  // S32 A1: scenario PDF export. Returns a 15-min presigned URL.
  exportTaxScenarioPdf: (id: string) =>
    apiRequest<{
      fileName: string;
      fileKey: string;
      downloadUrl: string;
      expiresInSeconds: number;
      bytes: number;
      scenarioId: string;
      scenarioName: string;
      taxYear: number;
    }>(`/tax/scenarios/${id}/pdf`, { method: 'POST', body: {} }),

  // S31: IRC citation registry. Public to any authed user.
  getTaxCitations: (params?: { keys?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<TaxCitationRegistry>(`/tax/citations${qs}`);
  },

  // S33 P1: public W-9 upload (NO auth — token is the gate). These
  // endpoints are called from the unauthenticated /w9-upload/:token page;
  // raw apiRequest is fine because the supervisor PUBLIC_RESOURCES set
  // explicitly allows them through without a JWT.
  startW9PublicUpload: (token: string) =>
    apiRequest<{
      uploadUrl: string;
      contentType: string;
      expiresInSeconds: number;
      key: string;
      contractorName: string;
      entityName: string;
    }>(`/w9-upload/${token}`, { method: 'POST', body: {} }),
  completeW9PublicUpload: (token: string, key: string) =>
    apiRequest<{ success: true; contractorId: string }>(
      `/w9-upload/${token}/complete`, { method: 'POST', body: { key } }
    ),

  // S32 C2: white-label advisor console
  getAdvisorClients: () =>
    apiRequest<{ clients: AdvisorClient[] }>('/advisor/clients'),
  getAdvisorClientDetail: (orgId: string) =>
    apiRequest<AdvisorClientDetail>(`/advisor/clients/${orgId}`),
  getAdvisorPortfolio: () =>
    apiRequest<AdvisorPortfolio>('/advisor/portfolio'),
  getAdvisorBrandSettings: () =>
    apiRequest<{ settings: AdvisorBrandSettings | null }>('/advisor/brand-settings'),
  updateAdvisorBrandSettings: (body: Partial<AdvisorBrandSettings>) =>
    apiRequest<{ settings: AdvisorBrandSettings }>('/advisor/brand-settings', { method: 'PUT', body }),
  // S33 P0 C2 Phase 2: per-entity drill-in
  getAdvisorClientEntities: (orgId: string) =>
    apiRequest<AdvisorClientEntities>(`/advisor/clients/${orgId}/entities`),
  getAdvisorEntityDrillIn: (orgId: string, eid: string) =>
    apiRequest<AdvisorEntityDrillIn>(`/advisor/clients/${orgId}/entities/${eid}`),
  // S33 P0 C2 Phase 2: logo upload (presigned PUT direct to S3)
  getAdvisorLogoUploadUrl: (extension: string) =>
    apiRequest<{ uploadUrl: string; key: string; contentType: string; expiresInSeconds: number }>(
      '/advisor/brand-settings/logo-url', { method: 'POST', body: { extension } }
    ),
  // S33 Session C: advisor_admin writes — only callable when linkRole === 'advisor_admin'.
  // Backend enforces; UI gates the buttons via the linkRole returned in /advisor/clients.
  advisorCreateJournalEntry: (orgId: string, eid: string, body: { entryDate: string; memo: string; accountLabel?: string; debitCents?: number; creditCents?: number }) =>
    apiRequest<{ entry: { id: string; entry_date: string; memo: string; debit_cents: number | null; credit_cents: number | null }; entity: { id: string; legalName: string } }>(
      `/advisor/clients/${orgId}/entities/${eid}/journal-entry`, { method: 'POST', body }
    ),
  advisorListJournalEntries: (orgId: string, eid: string) =>
    apiRequest<{ entries: Array<{ id: string; entry_date: string; account_label: string | null; debit_cents: number | null; credit_cents: number | null; memo: string; created_at: string }> }>(
      `/advisor/clients/${orgId}/entities/${eid}/journal-entries`
    ),
  advisorCreateExpense: (orgId: string, eid: string, body: { vendor: string; amount: number; expenseDate: string; category?: string; ircCode?: string; businessPurpose?: string; deductibilityPct?: number }) =>
    apiRequest<{ expense: { id: string; vendor: string; amount: string; expense_date: string }; entity: { id: string; legalName: string } }>(
      `/advisor/clients/${orgId}/entities/${eid}/expenses`, { method: 'POST', body }
    ),
  advisorUpdateExpense: (orgId: string, eid: string, expenseId: string, body: Partial<{ category: string; ircCode: string; businessPurpose: string; deductibilityPct: number; complianceStatus: string }>) =>
    apiRequest<{ expense: { id: string; category: string | null; irc_code: string | null } }>(
      `/advisor/clients/${orgId}/entities/${eid}/expenses/${expenseId}`, { method: 'PATCH', body }
    ),

  // S33 Session C: API keys (third-party integration). Cognito-authed at
  // /me/api-keys; the keys themselves authenticate at /api/v1/* via Bearer.
  listApiKeys: () =>
    apiRequest<{ keys: Array<{ id: string; prefix: string; name: string; scopes: string[]; lastUsedAt: string | null; revokedAt: string | null; createdAt: string }> }>(
      '/me/api-keys'
    ),
  createApiKey: (body: { name: string; scopes: string[] }) =>
    apiRequest<{ key: { id: string; prefix: string; name: string; scopes: string[]; createdAt: string }; fullKey: string; warning: string }>(
      '/me/api-keys', { method: 'POST', body }
    ),
  revokeApiKey: (id: string) =>
    apiRequest<{ revoked: true; prefix: string }>(`/me/api-keys/${id}`, { method: 'DELETE' }),

  // S39 P3 #7: queryNexus removed — backed nlq-agent which is retired.
  // /query is now the Ask Nexus AI page (Query.tsx -> useChatStore ->
  // /v1/ai/chat through ai-chat-agent).

  // Costs
  getCurrentCosts: () =>
    apiRequest('/costs/current'),

  // Mileage
  createMileageTrip: (data: Record<string, unknown>) =>
    apiRequest('/mileage', { method: 'POST', body: data }),

  listMileageTrips: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/mileage${qs}`);
  },

  // Compensation
  calculateCompensation: (data: Record<string, unknown>) =>
    apiRequest('/compensation/calculate', { method: 'POST', body: data }),

  getCompensationPolicy: () =>
    apiRequest('/compensation/policy'),

  // FMV
  analyzeFMV: (data: Record<string, unknown>) =>
    apiRequest('/fmv/analyze', { method: 'POST', body: data }),

  getFMVBenchmarks: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/fmv/benchmarks${qs}`);
  },

  // S39 P3 #7: Legacy /policy-docs/* functions removed — these hit the
  // retired policy-docs-agent Lambda. Policy document workflows now go
  // through the supervisor's /policy-documents/* routes (defined below
  // around line 2933 — listPolicyDocuments / getPolicyDocument /
  // updatePolicyDocument / deletePolicyDocument / generatePolicyDocument).

  // Admin
  getAdminDashboard: () =>
    apiRequest('/admin/dashboard', { skipEntityHeader: true }),

  getAdminTenants: () =>
    apiRequest('/admin/tenants', { skipEntityHeader: true }),

  getAdminHealth: () =>
    apiRequest('/admin/health', { skipEntityHeader: true }),

  /** Sprint 1.5 (2026-05-28). Nexus-only AWS spend breakdown via Cost Explorer.
   *  Cached 60min in cognito-proxy-agent (CE charges $0.01/call). Excludes
   *  KG-legacy services + org-shared services until aws:cloudformation:stack-name
   *  cost allocation tag finishes backfilling (~24h from activation). */
  getAdminCostBreakdown: () =>
    apiRequest<{
      accountId: string;
      scope: 'nexus';
      monthKey: string;
      asOf: string;
      mtdTotalUsd: number;
      byService: Array<{ service: string; mtdCostUsd: number }>;
      excludedServices: string[];
      tagBackfillNote: string;
      fromCache: boolean;
      fetchedAt?: string;
    }>('/admin/cost-breakdown', { skipEntityHeader: true }),

  getAdminAgentHealth: () =>
    apiRequest('/admin/health/agents', { skipEntityHeader: true }),

  getAdminAgentDetail: (name: string) =>
    apiRequest(`/admin/health/agents/${encodeURIComponent(name)}`, { skipEntityHeader: true }),

  // Admin user management (MFA-gated for writes)
  listAdminUsers: (params?: { q?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.append('q', params.q);
    if (params?.limit) qs.append('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiRequest<{
      users: Array<{
        id: string; cognito_id: string; email: string; full_name: string | null;
        is_platform_admin: boolean; is_active: boolean;
        created_at: string; updated_at: string;
        entity_count: number; any_membership_active: number;
      }>;
      matchCount: number;
      limit: number;
      searchApplied: string | null;
    }>(`/admin/users${suffix}`, { skipEntityHeader: true });
  },

  getAdminUserDetail: (id: string) =>
    apiRequest<{
      user: {
        id: string; cognito_id: string; email: string; full_name: string | null;
        is_platform_admin: boolean; is_active: boolean;
        created_at: string; updated_at: string;
        specialty: string | null; state: string | null;
      };
      memberships: Array<{
        entity_id: string; role: string; is_active: boolean; created_at: string;
        legal_name: string; tax_election: string; state_of_formation: string | null;
        org_name: string | null;
      }>;
      cognito: {
        userStatus?: string; enabled?: boolean;
        userCreateDate?: string; userLastModifiedDate?: string;
        mfaEnabled?: boolean; preferredMfa?: string | null; enabledMfas?: string[];
        error?: string; errorCode?: string;
      } | null;
      recentAudit: Array<{
        timestamp: string; agent_name: string; action: string; result: string; notes: string | null;
      }>;
    }>(`/admin/users/${id}`, { skipEntityHeader: true }),

  adminForceLogout: (id: string, reason?: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/force-logout`, { method: 'POST', skipEntityHeader: true, body: { reason } }
    ),

  adminResetPassword: (id: string, reason?: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/reset-password`, { method: 'POST', skipEntityHeader: true, body: { reason } }
    ),

  adminDemoReset: (entityId: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/demo-reset/${entityId}`, { method: 'POST', skipEntityHeader: true }
    ),

  adminResetMfa: (id: string, reason: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/reset-mfa`, { method: 'DELETE', skipEntityHeader: true, body: { reason } }
    ),

  adminResendVerification: (id: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/resend-verification`, { method: 'POST', skipEntityHeader: true }
    ),

  adminTogglePlatformAdmin: (id: string, enabled: boolean, reason: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/platform-admin`, { method: 'PATCH', skipEntityHeader: true, body: { enabled, reason } }
    ),

  adminBanUser: (id: string, reason: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/ban`, { method: 'POST', skipEntityHeader: true, body: { reason } }
    ),

  adminUnbanUser: (id: string, reason: string) =>
    apiRequest<{ success: boolean; message: string }>(
      `/admin/users/${id}/unban`, { method: 'POST', skipEntityHeader: true, body: { reason } }
    ),

  // S28: typed-confirm required. confirmText must equal "DELETE <email>"
  // — the backend re-validates against the target's actual email.
  adminDeleteAccount: (id: string, reason: string, confirmText: string) =>
    apiRequest<{ success: boolean; message: string; entityCount: number; cascadeWarnings: unknown[] }>(
      `/admin/users/${id}`, { method: 'DELETE', skipEntityHeader: true, body: { reason, confirmText } }
    ),

  // S28: Step-up auth flow. Mints a 15-min sliding token after the user
  // re-authenticates with password + TOTP. Token goes in sessionStorage
  // (per-tab; clears on close) and rides on every /admin/* request via
  // the X-Admin-Step-Up header (auto-injected in apiRequest).
  adminStepUpInitiate: (password: string, totpCode: string) =>
    apiRequest<{
      token: string;
      sessionId: string;
      expiresAt: string;
      hardExpiresAt: string;
    }>('/admin/step-up', { method: 'POST', skipEntityHeader: true, body: { password, totpCode } }),

  adminStepUpStatus: () =>
    apiRequest<{
      active: boolean;
      sessionId?: string;
      expiresAt?: string;
      hardExpiresAt?: string;
      lastUsedAt?: string;
      useCount?: number;
    }>('/admin/step-up/status', { skipEntityHeader: true }),

  adminStepUpRefresh: () =>
    apiRequest<{
      active: boolean;
      sessionId?: string;
      expiresAt?: string;
      hardExpiresAt?: string;
      error?: string;
    }>('/admin/step-up/refresh', { method: 'POST', skipEntityHeader: true }),

  adminStepUpRevoke: (revokeAll = false) =>
    apiRequest<{ revoked: boolean }>(
      '/admin/step-up/revoke', { method: 'POST', skipEntityHeader: true, body: { revokeAll } }
    ),

  // Section 105
  submitSection105: (data: Record<string, unknown>) =>
    apiRequest('/section-105', { method: 'POST', body: data }),

  listSection105: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/section-105${qs}`);
  },

  getSection105: (id: string) =>
    apiRequest(`/section-105/${id}`),

  reviewSection105: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/section-105/${id}/review`, { method: 'PATCH', body: data }),

  approveSection105: (id: string) =>
    apiRequest(`/section-105/${id}/approve`, { method: 'PATCH', body: {} }),

  reimburseSection105: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/section-105/${id}/reimburse`, { method: 'PATCH', body: data }),

  rejectSection105: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/section-105/${id}/reject`, { method: 'PATCH', body: data }),

  // Contractors
  createContractor: (data: Record<string, unknown>) =>
    apiRequest('/contractors', { method: 'POST', body: data }),

  /**
   * IRS FIRE 1099 export. Generates a fixed-width text file per
   * Publication 1220 for all contractors paid $600+ in the given year,
   * uploads it to S3, and returns a 15-minute presigned download URL.
   * User uploads the file to fire.irs.gov with their Transmitter Control
   * Code to file electronically.
   *
   * formType selects the 1099 variant: NEC (default, nonemployee comp),
   * MISC (rents/royalties/attorney fees), INT (interest), DIV (dividends),
   * R (retirement distributions). For non-NEC variants the same
   * contractors list is used as the payee source; users may need to edit
   * the file before upload to reallocate Amount Code boxes.
   */
  exportFire1099: (year: number, formType: '1099-NEC' | '1099-MISC' | '1099-INT' | '1099-DIV' | '1099-R' = '1099-NEC') => {
    const ft = formType.replace(/^1099-/, '');
    const qs = new URLSearchParams({ year: String(year), formType: ft }).toString();
    return apiRequest<{
      year: number;
      formType: string;
      payeeCount: number;
      totalAmount: number;
      fileName: string;
      downloadUrl: string;
      expiresInSeconds: number;
      note: string;
    }>(`/contractors/fire-export?${qs}`);
  },

  listContractors: (params?: { classification?: string; active?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/contractors${qs}`);
  },

  getContractor: (id: string) =>
    apiRequest(`/contractors/${id}`),

  updateContractor: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/contractors/${id}`, { method: 'PATCH', body: data }),

  // S32 B1: explicit W-9 re-send (1 per 24h per contractor).
  // Returns 429 if rate-limited, 502 on Mailgun failure, 200 on success or
  // NotConfigured (the latter still stamps w9_requested_at + audit_log).
  requestW9: (contractorId: string) =>
    apiRequest<{
      success: true;
      contractorId: string;
      email: string;
      requestedAt: string;
      mailgunReason: string | null;
    }>(`/contractors/${contractorId}/request-w9`, { method: 'POST', body: {} }),

  createContractorPayment: (contractorId: string, data: Record<string, unknown>) =>
    apiRequest(`/contractors/${contractorId}/payments`, { method: 'POST', body: data }),

  listContractorPayments: (contractorId: string) =>
    apiRequest(`/contractors/${contractorId}/payments`),

  generate1099NEC: (contractorId: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/contractors/${contractorId}/1099-nec${qs}`);
  },

  get1099Summary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/contractors/1099-summary${qs}`);
  },

  // ---------------------------------------------------------------
  // 1099-INT / 1099-DIV / 1099-R (S30 Item 6, migrations 052-054)
  // ---------------------------------------------------------------
  // Each form has list-by-year + create + get + patch + delete. The
  // `recipientContractorId` must be a contractor row already classified
  // 'interest_recipient' / 'shareholder' / 'distributee' respectively.

  listInterestPayments: (year: number) =>
    apiRequest<{ taxYear: number; payments: Record<string, unknown>[] }>(
      `/tax/1099/int?year=${year}`
    ),
  createInterestPayment: (data: Record<string, unknown>) =>
    apiRequest<{ payment: Record<string, unknown> }>(
      `/tax/1099/int`, { method: 'POST', body: data }
    ),
  getInterestPayment: (id: string) =>
    apiRequest<{ payment: Record<string, unknown> }>(`/tax/1099/int/${id}`),
  updateInterestPayment: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ payment: Record<string, unknown> }>(
      `/tax/1099/int/${id}`, { method: 'PATCH', body: data }
    ),
  deleteInterestPayment: (id: string) =>
    apiRequest<{ deleted: true; id: string }>(`/tax/1099/int/${id}`, { method: 'DELETE' }),

  listDividendDistributions: (year: number) =>
    apiRequest<{ taxYear: number; distributions: Record<string, unknown>[] }>(
      `/tax/1099/div?year=${year}`
    ),
  createDividendDistribution: (data: Record<string, unknown>) =>
    apiRequest<{ distribution: Record<string, unknown> }>(
      `/tax/1099/div`, { method: 'POST', body: data }
    ),
  getDividendDistribution: (id: string) =>
    apiRequest<{ distribution: Record<string, unknown> }>(`/tax/1099/div/${id}`),
  updateDividendDistribution: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ distribution: Record<string, unknown> }>(
      `/tax/1099/div/${id}`, { method: 'PATCH', body: data }
    ),
  deleteDividendDistribution: (id: string) =>
    apiRequest<{ deleted: true; id: string }>(`/tax/1099/div/${id}`, { method: 'DELETE' }),

  listRetirementDistributions: (year: number) =>
    apiRequest<{ taxYear: number; distributions: Record<string, unknown>[] }>(
      `/tax/1099/r?year=${year}`
    ),
  createRetirementDistribution: (data: Record<string, unknown>) =>
    apiRequest<{ distribution: Record<string, unknown> }>(
      `/tax/1099/r`, { method: 'POST', body: data }
    ),
  getRetirementDistribution: (id: string) =>
    apiRequest<{ distribution: Record<string, unknown> }>(`/tax/1099/r/${id}`),
  updateRetirementDistribution: (id: string, data: Record<string, unknown>) =>
    apiRequest<{ distribution: Record<string, unknown> }>(
      `/tax/1099/r/${id}`, { method: 'PATCH', body: data }
    ),
  deleteRetirementDistribution: (id: string) =>
    apiRequest<{ deleted: true; id: string }>(`/tax/1099/r/${id}`, { method: 'DELETE' }),

  // Clients
  createClient: (data: Record<string, unknown>) =>
    apiRequest('/clients', { method: 'POST', body: data }),

  listClients: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/clients${qs}`);
  },

  getClient: (id: string) =>
    apiRequest(`/clients/${id}`),

  updateClient: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/clients/${id}`, { method: 'PATCH', body: data }),

  // Invoices
  createInvoice: (data: Record<string, unknown>) =>
    apiRequest('/invoices', { method: 'POST', body: data }),

  listInvoices: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/invoices${qs}`);
  },

  getInvoice: (id: string) =>
    apiRequest(`/invoices/${id}`),

  updateInvoice: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/invoices/${id}`, { method: 'PATCH', body: data }),

  recordInvoicePayment: (invoiceId: string, data: Record<string, unknown>) =>
    apiRequest(`/invoices/${invoiceId}/payments`, { method: 'POST', body: data }),

  getAgingReport: () =>
    apiRequest('/invoices/aging'),

  // Route calculation (geocode + distance + tolls in one call)
  calculateRoute: (data: Record<string, unknown>) =>
    apiRequest('/mileage/calculate-route', { method: 'POST', body: data }),

  // Toll estimation (hybrid: crowd data + HERE Maps API)
  estimateToll: (data: Record<string, unknown>) =>
    apiRequest('/mileage/toll-estimate', { method: 'POST', body: data }),

  reportToll: (data: Record<string, unknown>) =>
    apiRequest('/mileage/toll-report', { method: 'POST', body: data }),

  // Saved Routes (one-tap trip logging)
  listSavedRoutes: () =>
    apiRequest('/mileage/saved-routes'),

  createSavedRoute: (data: Record<string, unknown>) =>
    apiRequest('/mileage/saved-routes', { method: 'POST', body: data }),

  updateSavedRoute: (id: string, data: Record<string, unknown>) =>
    apiRequest(`/mileage/saved-routes/${id}`, { method: 'PATCH', body: data }),

  deleteSavedRoute: (id: string) =>
    apiRequest(`/mileage/saved-routes/${id}`, { method: 'DELETE' }),

  useSavedRoute: (id: string) =>
    apiRequest(`/mileage/saved-routes/${id}/use`, { method: 'POST' }),

  // Beta gate — validates code server-side against Secrets Manager (no auth required)
  validateBetaCode: (code: string) =>
    apiRequest<{ valid: boolean; error?: string }>('/auth/beta-validate', {
      method: 'POST', body: { code }, skipAuth: true, skipEntityHeader: true,
    }),

  // Ask Nexus AI
  aiChat: (message: string, conversationId?: string | null) =>
    apiRequest('/ai/chat', { method: 'POST', body: { message, conversationId } }),

  aiListConversations: () =>
    apiRequest('/ai/conversations'),

  aiGetMessages: (conversationId: string) =>
    apiRequest(`/ai/conversations/${conversationId}/messages`),

  aiDeleteConversation: (conversationId: string) =>
    apiRequest(`/ai/conversations/${conversationId}`, { method: 'DELETE' }),

  aiGetUsage: () =>
    apiRequest('/ai/usage'),

  // Reports - additional
  getBalanceSheet: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/reports/balance-sheet${qs}`);
  },

  getEBITDA: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest(`/reports/ebitda${qs}`);
  },

  // ---- Contracts + unified documents (Phase 2e, migration 030) ----
  listContracts: (params?: { status?: string; contractType?: string; counterparty?: string; expiringWithinDays?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ contracts: Contract[] }>(`/contracts${qs}`);
  },
  getContract: (id: string) =>
    apiRequest<{ contract: ContractWithUrgency }>(`/contracts/${id}`),
  createContract: (data: Partial<Contract> & { contractType: string; title: string; counterpartyName: string }) =>
    apiRequest<{ contract: Contract }>('/contracts', { method: 'POST', body: data as Record<string, unknown> }),
  updateContract: (id: string, data: Partial<Contract>) =>
    apiRequest<{ contract: Contract }>(`/contracts/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deleteContract: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/contracts/${id}`, { method: 'DELETE' }),
  renewContract: (id: string, data: Partial<Contract>) =>
    apiRequest<{ contract: Contract; replaced: string }>(`/contracts/${id}/renew`, { method: 'POST', body: data as Record<string, unknown> }),

  listUnifiedDocuments: (params?: { sourceType?: string; category?: string; status?: string; q?: string; expiringWithinDays?: string }) => {
    // URLSearchParams(Record<string,string>) emits keys whose values are undefined
    // as the literal string "undefined". Filter them out first.
    const clean: Record<string, string> = {};
    if (params) for (const [k, v] of Object.entries(params)) if (v != null && v !== '') clean[k] = v;
    const qs = Object.keys(clean).length ? '?' + new URLSearchParams(clean).toString() : '';
    return apiRequest<{ documents: UnifiedDocument[]; matchCount?: number }>(`/documents${qs}`);
  },
  listDocumentRenewals: (windowDays = 90) =>
    apiRequest<{ renewals: DocumentRenewal[]; windowDays: number }>(`/documents/renewals?windowDays=${windowDays}`),

  // Inbound email (Phase 1, 2026-06-14)
  listDocumentRegistry: (category?: string) =>
    apiRequest<{ documents: RegistryDocument[] }>(`/documents/registry${category && category !== 'all' ? `?category=${encodeURIComponent(category)}` : ''}`),
  listInboundMessages: (lane?: string) =>
    apiRequest<{ messages: InboundMessage[]; counts: Record<string, number> }>(`/inbound/messages${lane && lane !== 'all' ? `?lane=${encodeURIComponent(lane)}` : ''}`),
  fileInboundMessage: (id: string) =>
    apiRequest<{ ok: boolean; status?: string; alreadyFiled?: boolean }>(`/inbound/messages/${id}/file`, { method: 'POST' }),
  dismissInboundMessage: (id: string, reason?: string) =>
    apiRequest<{ ok: boolean; status: string }>(`/inbound/messages/${id}/dismiss`, { method: 'POST', body: { reason } }),
  listInboundRoutes: () =>
    apiRequest<{ routes: InboundRoute[] }>(`/inbound/routes`),
  mintInboundRoute: (purpose: string, allowedSenders?: string[]) =>
    apiRequest<{ address: string; purpose: string; existing: boolean }>(`/inbound/routes`, { method: 'POST', body: { purpose, allowedSenders } }),
  inboundHandleStatus: (handle: string) =>
    apiRequest<InboundHandleStatus>(`/inbound/handle?handle=${encodeURIComponent(handle)}`),
  setInboundHandle: (handle: string) =>
    apiRequest<{ ok: boolean; handle: string; addresses: InboundHandleAddress[] }>(`/inbound/handle`, { method: 'POST', body: { handle } }),

  semanticSearchDocuments: (query: string, limit = 20) =>
    apiRequest<{
      mode: 'semantic' | 'ilike';
      fallbackReason?: string;
      query: string;
      documents: (UnifiedDocument & { match_excerpt?: string | null; similarity?: number })[];
      matchCount: number;
    }>(`/documents/semantic-search`, { method: 'POST', body: { query, limit } }),

  listPolicyDocuments: (params?: { status?: string; docType?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ documents: PolicyDocument[] }>(`/policy-documents${qs}`);
  },
  getPolicyDocument: (id: string) =>
    apiRequest<{ document: PolicyDocument & { content: string | null } }>(`/policy-documents/${id}`),
  updatePolicyDocument: (id: string, data: Partial<PolicyDocument>) =>
    apiRequest<{ document: PolicyDocument }>(`/policy-documents/${id}`, { method: 'PATCH', body: data as Record<string, unknown> }),
  deletePolicyDocument: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/policy-documents/${id}`, { method: 'DELETE' }),
  generatePolicyDocument: (templateKey: string) =>
    apiRequest<{ document: PolicyDocument }>(`/policy-documents/generate`, { method: 'POST', body: { templateKey } }),
  markPolicyCounselReviewed: (id: string, payload: PolicyCounselReviewPayload) =>
    apiRequest<{ document: PolicyDocument }>(`/policy-documents/${id}/counsel-review`, {
      method: 'POST',
      body: payload as unknown as Record<string, unknown>,
    }),

  // NS4 R9 — bulk employee import.
  bulkImportEmployees: (employees: Array<Record<string, unknown>>, dryRun = false) =>
    apiRequest<{
      dryRun: boolean;
      totalRows: number;
      successCount: number;
      skippedCount: number;
      errorCount: number;
      results: Array<{
        rowIndex: number;
        firstName?: string;
        lastName?: string;
        email?: string;
        status: 'created' | 'would_create' | 'skipped' | 'error';
        id?: string;
        error?: string;
        reason?: string;
      }>;
    }>('/employees/bulk-import', {
      method: 'POST', body: { employees, dryRun } as unknown as Record<string, unknown>,
    }),

  // NS4 R9 — contract amendments (mig 074).
  listContractAmendments: (contractId: string) =>
    apiRequest<{ chain: ContractChainEntry[] }>(`/contracts/${contractId}/amendments`),
  amendContract: (contractId: string, data: ContractAmendInput) =>
    apiRequest<{ contract: ContractRow; supersededId: string; amendmentNumber: number }>(
      `/contracts/${contractId}/amend`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  // NS4 R9 — contract signatures (mig 074).
  listContractSignatures: (contractId: string) =>
    apiRequest<{ signatures: ContractSignatureRow[] }>(`/contracts/${contractId}/signatures`),
  signContract: (contractId: string, data: ContractSignInput) =>
    apiRequest<{
      signature: { id: string; signed_at: string; signed_content_sha256: string };
      contentHash: string;
      disclaimer: string;
    }>(`/contracts/${contractId}/sign`, {
      method: 'POST', body: data as unknown as Record<string, unknown>,
    }),

  // NS4 R9 — notification preferences.
  listNotificationPreferences: () =>
    apiRequest<{
      preferences: Array<{
        eventType: string;
        label: string;
        emailEnabled: boolean;
        inAppEnabled: boolean;
        smsEnabled: boolean;
      }>;
    }>('/me/notification-preferences'),
  updateNotificationPreferences: (updates: Array<{
    eventType: string;
    emailEnabled?: boolean;
    inAppEnabled?: boolean;
    smsEnabled?: boolean;
  }>) =>
    apiRequest<{ upserted: number }>('/me/notification-preferences', {
      method: 'PATCH', body: { updates } as unknown as Record<string, unknown>,
    }),

  // SMS expense-capture opt-in (#2 build wave). The real opt-in screen the AWS
  // toll-free verification points at.
  getSmsCapture: () =>
    apiRequest<{ phone: string | null; status: string | null; consentAt: string | null }>('/me/sms-capture'),
  saveSmsCapture: (data: { phone?: string; consent?: boolean; consentText?: string; enabled?: boolean }) =>
    apiRequest<{ phone: string | null; status: string | null; consentAt: string | null }>(
      '/me/sms-capture', { method: 'POST', body: data }),

  // Founder-only plan switcher (platform_admin) — flip the org tier to test gated features.
  getTestTier: () =>
    apiRequest<{ currentTier: string | null; orgId: string; plans: Array<{ key: string; name: string; price: number | null }> }>('/me/test-tier'),
  setTestTier: (tier: string) =>
    apiRequest<{ tier: string; orgId: string }>('/me/test-tier', { method: 'POST', body: { tier } }),

  // NS4 R7 — global compliance_rule_facts registry (read-only).
  listComplianceRuleFacts: (params?: { includeRetired?: boolean }) => {
    const qs = params?.includeRetired ? '?includeRetired=true' : '';
    return apiRequest<{ facts: ComplianceRuleFact[] }>(`/compliance-rule-facts${qs}`);
  },

  // NS4 R7 — fixed-asset register (mig 073).
  listFixedAssets: (params?: { activeOnly?: boolean; year?: number }) => {
    const sp = new URLSearchParams();
    if (params?.activeOnly) sp.set('activeOnly', 'true');
    if (params?.year) sp.set('year', String(params.year));
    const qs = sp.toString() ? `?${sp.toString()}` : '';
    return apiRequest<{ assets: FixedAsset[]; year?: number }>(`/fixed-assets${qs}`);
  },
  createFixedAsset: (data: FixedAssetInput) =>
    apiRequest<{ asset: FixedAsset }>('/fixed-assets', {
      method: 'POST', body: data as unknown as Record<string, unknown>,
    }),
  updateFixedAsset: (id: string, data: Partial<FixedAssetInput>) =>
    apiRequest<{ asset: FixedAsset }>(`/fixed-assets/${id}`, {
      method: 'PATCH', body: data as unknown as Record<string, unknown>,
    }),
  deleteFixedAsset: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/fixed-assets/${id}`, { method: 'DELETE' }),

  // NS4 R7 — S-corp formation readiness check.
  getEntityReadiness: () =>
    apiRequest<{
      applicable: boolean;
      reason?: string;
      entity: { id: string; legal_name: string; tax_election: string };
      overallStatus?: 'ready' | 'pending_review' | 'overdue_review' | 'blocked';
      summary?: {
        total: number; ready: number; missing: number; unsigned: number;
        pendingCounselReview: number; counselReviewOverdue: number;
      };
      checks?: Array<{
        docType: string;
        label: string;
        required: boolean;
        status: 'ready' | 'missing' | 'unsigned' | 'pending_counsel_review' | 'counsel_review_overdue' | 'not_applicable';
        doc?: { doc_name: string; title: string | null; signed_date: string | null; counsel_reviewed_at: string | null; next_review_due_at: string | null };
      }>;
    }>('/entity/readiness'),

  // ---- Workflow approvals (Phase 2f, migration 031) ----
  listApprovalRules: (params?: { subjectType?: string; isActive?: 'true' | 'false' }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ rules: ApprovalRuleRow[] }>(`/approval-rules${qs}`);
  },
  createApprovalRule: (data: ApprovalRuleCreatePayload) =>
    apiRequest<{ rule: ApprovalRuleRow }>('/approval-rules', { method: 'POST', body: data as unknown as Record<string, unknown> }),
  updateApprovalRule: (id: string, data: Partial<ApprovalRuleCreatePayload> & { isActive?: boolean }) =>
    apiRequest<{ rule: ApprovalRuleRow }>(`/approval-rules/${id}`, { method: 'PATCH', body: data as unknown as Record<string, unknown> }),
  deleteApprovalRule: (id: string) =>
    apiRequest<{ deleted: boolean; id: string }>(`/approval-rules/${id}`, { method: 'DELETE' }),

  listApprovals: (params?: { status?: string; subjectType?: string; subjectId?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ approvals: ApprovalRow[] }>(`/approvals${qs}`);
  },
  listPendingApprovals: (params?: { subjectType?: string; requiredRole?: string }) => {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    return apiRequest<{ pending: PendingApprovalRow[] }>(`/approvals/pending${qs}`);
  },
  getApproval: (id: string) =>
    apiRequest<{ approval: ApprovalRow }>(`/approvals/${id}`),
  decideApproval: (id: string, decision: 'approved' | 'rejected', note?: string) =>
    apiRequest<{ approval: ApprovalRow }>(`/approvals/${id}/decide`, { method: 'POST', body: { decision, note } }),
  cancelApproval: (id: string) =>
    apiRequest<{ approval: ApprovalRow }>(`/approvals/${id}/cancel`, { method: 'POST' }),

  // --- Locums Classifications (Bundle 4 — IRS worker-classification record) ---
  // Audit refs:
  //   - nexus/docs/audits/PRE-FIX-AUDIT-2026-05-17-bundle-4-locums-classification.md
  //   - nexus/docs/audits/PHASE-0-REVERIFY-2026-05-17-SYNTHESIS.md
  getLocumsFactorsRegistry: () =>
    apiRequest<LocumsFactorsRegistryResponse>('/locums/factors-registry'),
  listLocumsClassifications: (params?: {
    perspective?: LocumsPerspective;
    counselReviewFlagged?: boolean;
    includeAll?: boolean;
  }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    return apiRequest<{ classifications: LocumsClassification[] }>(`/locums/classifications${qs}`);
  },
  getLocumsClassification: (id: string) =>
    apiRequest<{ classification: LocumsClassification }>(`/locums/classifications/${id}`),
  createLocumsClassification: (data: LocumsClassificationCreatePayload) =>
    apiRequest<{ classification: LocumsClassification }>(
      '/locums/classifications',
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),
  updateLocumsClassification: (id: string, data: Partial<LocumsClassificationCreatePayload> & { status?: 'draft' | 'finalized' }) =>
    apiRequest<{ classification: LocumsClassification }>(
      `/locums/classifications/${id}`,
      { method: 'PATCH', body: data as unknown as Record<string, unknown> }
    ),
  supersedeLocumsClassification: (
    oldId: string,
    data: { supersession_reason: string; new_classification: LocumsClassificationCreatePayload }
  ) =>
    apiRequest<{
      classification: LocumsClassification;
      superseded: { id: string; superseded_by_id: string; reason: string };
    }>(`/locums/classifications/${oldId}/supersede`, {
      method: 'POST',
      body: data as unknown as Record<string, unknown>,
    }),
  markLocumsClassificationCounselReview: (
    id: string,
    data: { counsel_review_completed_by: string; counsel_review_note?: string }
  ) =>
    apiRequest<{ classification: LocumsClassification }>(
      `/locums/classifications/${id}/counsel-review`,
      { method: 'POST', body: data as unknown as Record<string, unknown> }
    ),

  // --- Bundle 3: EFTPS Deposits + Form 945 (IRC §3406, §6302, §6656) ---
  listTaxDeposits: (params?: { year?: number; form_code?: string; includeVoided?: boolean }) => {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    return apiRequest<{ deposits: TaxDeposit[] }>(`/tax/eftps-deposits${qs}`);
  },
  recordTaxDeposit: (data: TaxDepositCreatePayload) =>
    apiRequest<{ deposit: TaxDeposit }>('/tax/eftps-deposits',
      { method: 'POST', body: data as unknown as Record<string, unknown> }),
  updateTaxDeposit: (id: string, data: Partial<TaxDepositCreatePayload>) =>
    apiRequest<{ deposit: TaxDeposit }>(`/tax/eftps-deposits/${id}`,
      { method: 'PATCH', body: data as unknown as Record<string, unknown> }),
  voidTaxDeposit: (id: string, reason: string) =>
    apiRequest<{ deposit: TaxDeposit }>(`/tax/eftps-deposits/${id}/void`,
      { method: 'POST', body: { reason } }),
  getDepositCadence: (year: number) =>
    apiRequest<DepositCadenceResponse>(`/tax/deposit-cadence/${year}`),
  setDepositCadence: (year: number, cadence: DepositCadence) =>
    apiRequest<DepositCadenceResponse>(`/tax/deposit-cadence/${year}`,
      { method: 'POST', body: { cadence } }),
  previewForm945: (year: number) =>
    apiRequest<Form945PreviewResponse>(`/tax/form-945/${year}/preview`),
  generateForm945Pdf: (year: number, variant: 'main' | 'schedule_a' | 'voucher' = 'main') =>
    apiRequest<{ url: string; filename: string; variant: string; taxYear: number; bytes: number; expires_in: number }>(
      `/tax/form-945/${year}?variant=${variant}`),
};

// --- Bundle 3 types ---

export type DepositCadence = 'monthly' | 'semi_weekly' | 'quarterly_safe_harbor';
export type TaxDepositFormCode = '945' | '941' | '940' | '1120-W';
export type TaxDepositStatus = 'recorded' | 'voided';

export interface TaxDeposit {
  id: string;
  entity_id: string;
  tax_year: number;
  form_code: TaxDepositFormCode;
  deposit_date: string;
  amount_cents: number;
  eftps_confirmation_number: string | null;
  liability_period_start: string | null;
  liability_period_end: string | null;
  notes: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  status: TaxDepositStatus;
}

export interface TaxDepositCreatePayload {
  tax_year: number;
  form_code?: TaxDepositFormCode;
  deposit_date: string;
  amount_cents: number;
  eftps_confirmation_number?: string;
  liability_period_start?: string;
  liability_period_end?: string;
  notes?: string;
}

export interface DepositCadenceResponse {
  tax_year: number;
  lookback_period: { year: number; start: string; end: string };
  lookback_total_cents: number;
  computed_cadence: DepositCadence | null;
  operator_cadence: DepositCadence | null;
  operator_cadence_set_at: string | null;
  operator_cadence_tax_year: number | null;
  effective_cadence: DepositCadence | null;
  cadence_requires_operator_decision: boolean;
  thresholds: {
    annual_de_minimis_cents: number;
    monthly_vs_semi_weekly_cents: number;
    next_day_cents: number;
  };
}

export interface Form945Lines {
  taxYear: number;
  line1: number;
  line2: number;
  line3: number;
  line4: number;
  line5: number;
  line6: number;
  line6a: number;
  line6b: number;
  line6c: string | null;
  line6d: string | null;
  line6e: string | null;
  line7: number[] | null;
  line7m: number;
  requires_schedule_a: boolean;
  schedule_a_reasons: string[];
}

export interface Form945PreviewResponse {
  tax_year: number;
  lines: Form945Lines;
  due_dates: { primary: string; safe_harbor: string };
  cadence: DepositCadence | null;
  had_next_day_accumulation: boolean;
  daily_liability_cents: Record<string, number>;
}

// --- Locums classification types ---

export type LocumsPerspective = 'our_1099_contractor' | 'our_engagement_as_contractor';
export type LocumsCounterpartyKind = 'staffing_company' | 'hospital' | 'group_practice' | 'other';
export type LocumsCompensationForm = 'hourly' | 'daily' | 'monthly' | 'per_shift' | 'project' | 'other';
export type LocumsFactorAnswer = 'yes' | 'no' | 'na' | 'unknown';
export type LocumsFactorDirection = 'employee' | 'contractor' | 'contextual';
export type LocumsFactorCategory = 'behavioral_control' | 'financial_control' | 'type_of_relationship';
export type LocumsRecommendation =
  | 'strong_employee_indicators'
  | 'strong_contractor_indicators'
  | 'mixed_indicators'
  | 'insufficient_data';
export type LocumsClassificationStatus = 'draft' | 'finalized' | 'superseded' | 'voided';

export interface LocumsFactor {
  id: string;
  number: number;
  category: LocumsFactorCategory;
  direction: LocumsFactorDirection;
  question: string;
  irc_citation: string;
  plain_help: string;
}

export interface LocumsFactorsRegistryResponse {
  registry_version: string;
  factors: LocumsFactor[];
  categories: Record<string, LocumsFactorCategory>;
  directions: Record<string, LocumsFactorDirection>;
  disclaimer: string;
}

export interface LocumsFactorAnswerEntry {
  factor_id: string;
  answer: LocumsFactorAnswer;
  answer_note?: string;
}

export interface LocumsCounselReviewReason {
  id: string;
  label: string;
  reason: string;
  citation: string;
}

export interface LocumsClassification {
  id: string;
  entity_id: string;
  counterparty_contractor_id: string | null;
  counterparty_name: string;
  counterparty_kind: LocumsCounterpartyKind;
  engagement_perspective: LocumsPerspective;
  engagement_role: string | null;
  engagement_start_date: string | null;
  engagement_end_date: string | null;
  engagement_hours_per_week: number | null;
  engagement_state: string | null;
  engagement_compensation_form: LocumsCompensationForm | null;
  factor_answers: LocumsFactorAnswerEntry[];
  score_employee_indicators: number | null;
  score_contractor_indicators: number | null;
  score_contextual_indicators: number | null;
  score_unknown_count: number | null;
  recommendation: LocumsRecommendation | null;
  section_530_claimed: boolean;
  section_530_reasonable_basis: string | null;
  section_530_substantive_consistency: boolean | null;
  section_530_reporting_consistency: boolean | null;
  counsel_review_flagged: boolean;
  counsel_review_reasons: LocumsCounselReviewReason[];
  counsel_review_completed_at: string | null;
  counsel_review_completed_by: string | null;
  counsel_review_note: string | null;
  registry_version: string;
  parent_classification_id: string | null;
  superseded_at: string | null;
  superseded_by_id: string | null;
  supersession_reason: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  status: LocumsClassificationStatus;
}

export interface LocumsClassificationCreatePayload {
  counterparty_contractor_id?: string | null;
  counterparty_name: string;
  counterparty_kind?: LocumsCounterpartyKind;
  engagement_perspective: LocumsPerspective;
  engagement_role?: string;
  engagement_start_date?: string;
  engagement_end_date?: string;
  engagement_hours_per_week?: number;
  engagement_state?: string;
  engagement_compensation_form?: LocumsCompensationForm;
  factor_answers: LocumsFactorAnswerEntry[];
  section_530_claimed?: boolean;
  section_530_reasonable_basis?: string;
  section_530_substantive_consistency?: boolean;
  section_530_reporting_consistency?: boolean;
  status?: 'draft' | 'finalized';
}

/** Check if we're in local mode (no backend configured) */
export function isInLocalMode(): boolean {
  return isLocalMode;
}
